import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, fromEvent, Observable, Subject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { WebSocketController } from '@services/web-socket.controller';
import { AppStateChangeService } from '@services/app-state-change.service';
import { getAudioConstraints, getVideoConstraints } from '@constants/app.constants';
import { FacingMode, WebrtcBaseService } from '@avatar/services/webrtc-base.service';
import { SubscriptionsBag } from '@utils/subscriptions-bag';
import { PlatformService } from '@services/platform.service';
import { NotificationType } from '@capacitor/haptics';
import { AudioService } from '@services/audio.service';
import { HapticsService } from '@services/haptics.service';
import { EventAudioCallCustomerReadyReconnect, EventAudioCallRequestReconnect } from '@services/audio-call.socket.interfaces';
import { AudioCallDeliveryStatus, AudioCallReliableMeta, AudioCallReliableSendResult, SOCKET_EVENT_NAMES } from '@services/web-socket.interfaces';
import { AudioCallConnectionDebugService, AudioCallConnectionDebugParams, AudioCallConnectionDebugTone } from '@services/audio-call-connection-debug.service';
import { AudioCallWebrtcDebugState } from '@services/audio-call-webrtc-debug.interface';
import { IosVideoCallBackgroundCameraService } from '@services/ios-video-call-background-camera.service';
import { AudioCallStatsSampler } from '@app/services/audio-call-stats';
import { CallSessionService } from '@call/services/call-session.service';

declare global {
  interface Window {
    webkitAudioContext?: typeof AudioContext;
  }
}

interface ReliableAudioCallProcessingResult {
  status: AudioCallDeliveryStatus;
  errorMessage?: string;
  error?: unknown;
}

interface MediaTrackDiagnostics {
  readonly id: string | null;
  readonly kind: string | null;
  readonly label: string | null;
  readonly readyState: MediaStreamTrackState | null;
  readonly enabled: boolean | null;
  readonly muted: boolean | null;
  readonly deviceId: string | null;
  readonly facingMode: string | null;
}

interface MediaElementDiagnostics {
  readonly paused: boolean | null;
  readonly muted: boolean | null;
  readonly readyState: number | null;
  readonly currentTime: number | null;
  readonly srcObjectTrackCount: number | null;
}

interface VideoCallTrackCapture {
  readonly videoTrack: MediaStreamTrack;
  readonly audioTrack: MediaStreamTrack | null;
}

@Injectable({
  providedIn: 'root'
})
export class WebrtcAudioService extends WebrtcBaseService implements OnDestroy {
  private readonly webSocketController: WebSocketController = inject(WebSocketController);
  private readonly ngZone: NgZone = inject(NgZone);
  private readonly appStateChangeService: AppStateChangeService = inject(AppStateChangeService);
  private readonly platformService: PlatformService = inject(PlatformService);
  private readonly audioService: AudioService = inject(AudioService);
  private readonly callSessionService: CallSessionService = inject(CallSessionService);
  private readonly hapticsService: HapticsService = inject(HapticsService);
  private readonly audioCallConnectionDebugService: AudioCallConnectionDebugService = inject(AudioCallConnectionDebugService);
  private readonly iosVideoCallBackgroundCameraService: IosVideoCallBackgroundCameraService =
    inject(IosVideoCallBackgroundCameraService);

  public peerConnection: RTCPeerConnection | null = null;
  public peerServerConfigDebug: { config?: { iceServers?: unknown; iceTransportPolicy?: unknown } } = environment.peerServerConfig;
  public streamStartTime: number = 0;
  public showButtonPlaySubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private readonly isConnectedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly isConnected$: Observable<boolean> = this.isConnectedSubject.asObservable();
  public withSpeaker: boolean = false;
  public isEarpieceSimulated: boolean = true;
  public isScreenSharing: boolean = false;
  private iceCandidatesBuffer: RTCIceCandidate[] = [];
  private readonly statsSampler = new AudioCallStatsSampler();
  private allCandidates: { local: RTCIceCandidate[]; remote: RTCIceCandidate[] } = { local: [], remote: [] };
  private localStream: MediaStream | null = null;
  private remoteStream: MediaStream | null = null;
  private remotePlaybackStream: MediaStream | null = null;
  private mediaElement: HTMLMediaElement | null = null;
  private audioCallEventSubscription: Subscription | null = null;
  private errorCallback: ((error: unknown) => void) | null = null;
  private changeCallback: (() => void) | null = null;
  private partnerId: number | null = null;
  private orderId: number | null = null;
  private callSessionAtPeerStart: object | null = null;
  private isCustomer: boolean = false;
  private isShared: boolean = false;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly reconnectDelayMs: number = 3000;
  private readonly reconnectCooldownMs: number = 5000;
  private readonly reconnectDeadlineMs: number = 30000;
  private lastReconnectAt: number = 0;
  private reconnectStartedAt: number = 0;
  private reconnectAttempts: number = 0;
  private readonly maxReconnectAttempts: number = 5;
  private readonly debugStateSubject: BehaviorSubject<AudioCallWebrtcDebugState> =
    new BehaviorSubject<AudioCallWebrtcDebugState>(this.buildDebugStateSnapshot());
  public readonly debugState$: Observable<AudioCallWebrtcDebugState> = this.debugStateSubject.asObservable();
  private readonly localPreviewStreamChangedSubject: Subject<void> = new Subject<void>();
  public readonly localPreviewStreamChanged$: Observable<void> = this.localPreviewStreamChangedSubject.asObservable();
  private reconnectFailedNotified: boolean = false;
  private readonly processedReliableMessageTtlMs: number = 120000;
  private processedReliableMessages: Map<string, number> = new Map();
  private processingReliableMessages: Map<string, Promise<ReliableAudioCallProcessingResult>> = new Map();
  private reliableAudioCallEventQueue: Promise<void> = Promise.resolve();
  private mediaInitialization: {peer: RTCPeerConnection; ready: Promise<void>} | null = null;
  private localOfferOperation: object | null = null;
  private readonly reconnectFailedSubject: Subject<void> = new Subject<void>();
  public readonly reconnectFailed$: Observable<void> = this.reconnectFailedSubject.asObservable();
  private playPending: Promise<void> | null = null;
  private mediaElementSb: SubscriptionsBag = new SubscriptionsBag();
  private audioContext: AudioContext | null = null;
  private leftSpeakerGainNode: GainNode | null = null;
  private rightSpeakerGainNode: GainNode | null = null;
  private stereoMergerNode: ChannelMergerNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private audioProcessingSourceStream: MediaStream | null = null;
  private destinationNode: MediaStreamAudioDestinationNode | null = null;
  private readonly earpieceLevels: Readonly<{ left: number; right: number }> = { left: 0.005, right: 0.05 };
  private iosNativeVideoCallPictureInPictureAudioMode: boolean = false;
  private isVideoToggleInProgress: boolean = false;
  private readonly localVideoFrameReadyTimeoutMs: number = 2500;
  private readonly videoReadyStateHaveCurrentData: number = 2;
  private localVideoTrack: MediaStreamTrack | null = null;
  private localPreviewStream: MediaStream | null = null;
  private localVideoPictureInPictureStream: MediaStream | null = null;
  private localVideoSender: RTCRtpSender | null = null;
  private iosBackgroundCameraOutgoingVideoTrack: MediaStreamTrack | null = null;
  private videoCallCameraFacing: FacingMode = 'user';
  private remoteStreamSubject: BehaviorSubject<MediaStream | null> = new BehaviorSubject<MediaStream | null>(null);
  public readonly remoteStream$: Observable<MediaStream | null> = this.remoteStreamSubject.asObservable();
  private readonly remoteVideoActiveSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly remoteVideoActive$: Observable<boolean> = this.remoteVideoActiveSubject.asObservable();
  private remoteSourceStream: MediaStream | null = null;
  private remoteVideoWasActiveForCurrentConnection: boolean = false;
  private screenShareStream: MediaStream | null = null;
  private screenShareTrack: MediaStreamTrack | null = null;
  private wasCameraEnabledBeforeScreenShare: boolean = false;
  private preservedCameraTrack: MediaStreamTrack | null = null;
  private preservedCameraStream: MediaStream | null = null;
  private hasWarmedUpAndroidAudioLabels: boolean = false;
  private warmUpAndroidAudioLabelsPromise: Promise<void> | null = null;
  private readonly onRemoteSourceRemoveTrack: (event: MediaStreamTrackEvent) => void = (event: MediaStreamTrackEvent): void => {
    this.removeRemoteTrack(event.track);
    if (typeof this.changeCallback === 'function') this.changeCallback();
  };
  private readonly remoteTrackListeners: Map<MediaStreamTrack, () => void> = new Map<MediaStreamTrack, () => void>();

  constructor() {
    super();
  }

  public ngOnDestroy(): void {
    this.stopPeerConnection();
  }

  public get isConnected(): boolean {
    return this.isConnectedSubject.value;
  }

  private set isConnected(value: boolean) {
    this.isConnectedSubject.next(value);
  }

  public get isExecutor(): boolean {
    return !this.isCustomer;
  }

  public getLocalPreviewStream(): MediaStream | null {
    const localVideoTrack: MediaStreamTrack | null = this.getLiveLocalVideoTrack();
    if (!localVideoTrack) {
      this.setLocalPreviewStream(null);
      return null;
    }

    const previewTrack: MediaStreamTrack | undefined = this.localPreviewStream?.getVideoTracks()[0];
    if (!this.localPreviewStream || !previewTrack || previewTrack.id !== localVideoTrack.id || previewTrack.readyState !== 'live') {
      this.setLocalPreviewStream(new MediaStream([localVideoTrack]));
    }

    return this.localPreviewStream;
  }

  public getLocalVideoPictureInPictureStream(): MediaStream | null {
    const videoTrack: MediaStreamTrack | null = this.getLocalVideoPictureInPictureTrack();
    if (!videoTrack) {
      this.localVideoPictureInPictureStream = null;
      return null;
    }

    const pictureInPictureTrack: MediaStreamTrack | undefined =
      this.localVideoPictureInPictureStream?.getVideoTracks()[0];
    if (!this.localVideoPictureInPictureStream
      || !pictureInPictureTrack
      || pictureInPictureTrack.id !== videoTrack.id
      || pictureInPictureTrack.readyState !== 'live') {
      this.localVideoPictureInPictureStream = new MediaStream([videoTrack]);
    }

    return this.localVideoPictureInPictureStream;
  }

  private setLocalPreviewStream(stream: MediaStream | null): void {
    if (this.localPreviewStream === stream) return;

    this.localPreviewStream = stream;
    this.localPreviewStreamChangedSubject.next();
  }

  public hasLocalVideoTrack(): boolean {
    return !!this.getLiveLocalVideoTrack();
  }

  public hasUsableLocalVideoTrack(): boolean {
    const track: MediaStreamTrack | null = this.getLiveLocalVideoTrack();
    return !!track && track.muted !== true;
  }

  public hasRemoteVideoTrack(): boolean {
    return !!this.remoteStream?.getVideoTracks().some((track: MediaStreamTrack): boolean => this.isActiveRemoteVideoTrack(track));
  }

  public get remoteVideoActive(): boolean {
    return this.remoteVideoActiveSubject.value;
  }

  public get remoteVideoWasActive(): boolean {
    return this.remoteVideoWasActiveForCurrentConnection;
  }

  public hasLiveRemoteVideoTrack(): boolean {
    return !!this.remoteStream?.getVideoTracks().some((track: MediaStreamTrack): boolean => track.readyState === 'live');
  }

  public getRemoteStream(): MediaStream | null {
    return this.remoteStream;
  }

  public logMediaDiagnostics(tag: string): void {
    const mediaElement: HTMLMediaElement | null = this.mediaElement;
    const mediaElementSrcObject: MediaProvider | null = mediaElement?.srcObject ?? null;
    const mediaElementTrackCount: number | null =
      mediaElementSrcObject instanceof MediaStream
        ? mediaElementSrcObject.getTracks().length
        : null;

    console.log('[AudioCall][MediaDiagnostics]', {
      tag,
      orderId: this.orderId,
      partnerId: this.partnerId,
      isConnected: this.isConnected,
      isScreenSharing: this.isScreenSharing,
      localAudioTracks: this.localStream?.getAudioTracks().map((track: MediaStreamTrack): MediaTrackDiagnostics => this.getTrackDiagnostics(track)) ?? [],
      localVideoTracks: this.localStream?.getVideoTracks().map((track: MediaStreamTrack): MediaTrackDiagnostics => this.getTrackDiagnostics(track)) ?? [],
      remoteAudioTracks: this.remoteStream?.getAudioTracks().map((track: MediaStreamTrack): MediaTrackDiagnostics => this.getTrackDiagnostics(track)) ?? [],
      remoteVideoTracks: this.remoteStream?.getVideoTracks().map((track: MediaStreamTrack): MediaTrackDiagnostics => this.getTrackDiagnostics(track)) ?? [],
      playbackAudioTracks: this.remotePlaybackStream?.getAudioTracks().map((track: MediaStreamTrack): MediaTrackDiagnostics => this.getTrackDiagnostics(track)) ?? [],
      playbackVideoTracks: this.remotePlaybackStream?.getVideoTracks().map((track: MediaStreamTrack): MediaTrackDiagnostics => this.getTrackDiagnostics(track)) ?? [],
      mediaElement: this.getMediaElementDiagnostics(mediaElement, mediaElementTrackCount),
    });
  }

  public getVideoCallCameraFacing(): FacingMode {
    return this.videoCallCameraFacing;
  }

  public setIosNativeVideoCallPictureInPictureAudioMode(isActive: boolean): void {
    if (!this.platformService.isIosApp) return;
    if (this.iosNativeVideoCallPictureInPictureAudioMode === isActive) return;

    this.iosNativeVideoCallPictureInPictureAudioMode = isActive;
    this.rebuildRemotePlaybackStream();
  }

  public getLocalVideoFacing(): FacingMode | null {
    const track: MediaStreamTrack | undefined = this.resolveFacingTrack()
      ?? this.localStream?.getVideoTracks().find((item: MediaStreamTrack): boolean => item.readyState === 'live');
    if (!track) return null;
    return this.getFacingModeFromTrack(track);
  }

  private getLiveLocalVideoTrack(): MediaStreamTrack | null {
    const track: MediaStreamTrack | undefined = this.localVideoTrack
      ?? this.localStream?.getVideoTracks().find((item: MediaStreamTrack): boolean => item.readyState === 'live');

    if (!track || track.readyState !== 'live') return null;
    return track;
  }

  private getLocalVideoPictureInPictureTrack(): MediaStreamTrack | null {
    const localVideoTrack: MediaStreamTrack | null = this.getLiveLocalVideoTrack();
    if (localVideoTrack) return localVideoTrack;

    const senderTrack: MediaStreamTrack | null = this.getLocalVideoSender()?.track ?? null;
    if (!senderTrack || senderTrack.readyState !== 'live') return null;
    return senderTrack;
  }

  private resolveFacingTrack(): MediaStreamTrack | null {
    if (this.isScreenSharing && this.preservedCameraTrack && this.preservedCameraTrack.readyState === 'live') {
      return this.preservedCameraTrack;
    }
    return this.localVideoTrack;
  }

  private getTrackDiagnostics(track: MediaStreamTrack | null | undefined): MediaTrackDiagnostics {
    if (!track) {
      return {
        id: null,
        kind: null,
        label: null,
        readyState: null,
        enabled: null,
        muted: null,
        deviceId: null,
        facingMode: null,
      };
    }

    let settings: MediaTrackSettings | null = null;
    try {
      settings = track.getSettings();
    } catch {
      settings = null;
    }

    const deviceId: string | null = typeof settings?.deviceId === 'string' && settings.deviceId.length > 0
      ? settings.deviceId
      : null;
    const facingModeRaw: unknown = settings?.facingMode;
    const facingMode: string | null = typeof facingModeRaw === 'string'
      ? facingModeRaw
      : Array.isArray(facingModeRaw)
        ? facingModeRaw.filter((item: unknown): item is string => typeof item === 'string').join(',')
        : null;

    return {
      id: track.id,
      kind: track.kind,
      label: track.label || null,
      readyState: track.readyState,
      enabled: track.enabled,
      muted: track.muted,
      deviceId,
      facingMode,
    };
  }

  private getMediaElementDiagnostics(
    mediaElement: HTMLMediaElement | null,
    srcObjectTrackCount: number | null,
  ): MediaElementDiagnostics {
    if (!mediaElement) {
      return {
        paused: null,
        muted: null,
        readyState: null,
        currentTime: null,
        srcObjectTrackCount: null,
      };
    }

    return {
      paused: mediaElement.paused,
      muted: mediaElement.muted,
      readyState: mediaElement.readyState,
      currentTime: mediaElement.currentTime,
      srcObjectTrackCount,
    };
  }

  private getFacingModeFromTrack(track: MediaStreamTrack): FacingMode | null {
    try {
      const settings: MediaTrackSettings = track.getSettings();
      const facingMode: unknown = settings.facingMode;
      const normalized: FacingMode | null = this.normalizeFacingModeValue(facingMode);
      if (normalized) return normalized;
    } catch {
      // ignore
    }

    const label: string = track.label ? track.label.toLowerCase() : '';
    if (!label) return null;
    if (label.includes('front') || label.includes('user') || label.includes('face') || label.includes('facetime')) {
      return 'user';
    }
    if (label.includes('back') || label.includes('rear') || label.includes('environment')) {
      return 'environment';
    }
    return null;
  }

  private normalizeFacingModeValue(value: unknown): FacingMode | null {
    if (value === 'user' || value === 'environment') {
      return value;
    }
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item === 'user' || item === 'environment') {
          return item;
        }
      }
    }
    return null;
  }

  private updateVideoCallFacingFromTrack(track: MediaStreamTrack): void {
    const actualFacing: FacingMode | null = this.getFacingModeFromTrack(track);
    if (actualFacing) {
      this.videoCallCameraFacing = actualFacing;
      this.iosVideoCallBackgroundCameraService.updateFacingMode(actualFacing);
    }
  }

  private shouldRetryVideoTrack(error: unknown): boolean {
    if (!(error instanceof DOMException) && !(error instanceof Error)) return false;
    const name = error.name;
    return name === 'NotReadableError' || name === 'TrackStartError' || name === 'AbortError';
  }

  private async stopLocalVideoTrackForRetry(): Promise<void> {
    const peer = this.peerConnection;
    if (!this.isCurrentPeer(peer)) return;
    const videoTrack: MediaStreamTrack | null = this.localVideoTrack;
    if (!videoTrack) return;

    await this.deactivateIosBackgroundCameraOutgoingVideoTrack();
    if (!this.isCurrentPeer(peer)) return;

    if (this.localVideoSender) {
      try {
        await this.localVideoSender.replaceTrack(null);
      } catch (error) {
        if (!this.isCurrentPeer(peer)) return;
        console.warn('replaceTrack(null) failed:', error);
      }
      if (!this.isCurrentPeer(peer)) return;
      this.disableVideoTransceiverSending(this.localVideoSender);
      try {
        this.localVideoSender.setStreams();
      } catch (error) {
        console.warn('Unable to clear sender streams:', error);
      }
    }

    try {
      if (this.localStream?.getVideoTracks().includes(videoTrack)) {
        this.localStream.removeTrack(videoTrack);
      }
    } catch (error) {
      console.warn('Failed to remove video track from local stream:', error);
    }

    try {
      videoTrack.stop();
    } catch {
      // ignore
    }

    this.localVideoTrack = null;
    this.setLocalPreviewStream(null);
  }

  public async initPeerConnection(
    partnerId: number,
    orderId: number,
    mediaElement: HTMLVideoElement | HTMLAudioElement,
    isCustomer: boolean,
    isShared: boolean,
    changeCallback?: () => void,
    errorHandler?: (error: unknown) => void,
  ): Promise<void> {
    if (this.peerConnection) {
      console.error('PeerConnection already initialized!');
      return;
    }

    this.partnerId = partnerId;
    this.orderId = orderId;
    const selectedOrder = this.callSessionService.selectedOrder;
    this.callSessionAtPeerStart = !isShared && selectedOrder?.id === orderId
      && selectedOrder.partnerId === partnerId && selectedOrder.isCustomer === isCustomer
      ? this.callSessionService.mediaSessionIdentity : null;
    this.mediaElement = mediaElement;
    this.isCustomer = isCustomer;
    this.isShared = isShared;
    this.reconnectAttempts = 0;
    this.reconnectStartedAt = 0;
    this.lastReconnectAt = 0;
    this.reconnectFailedNotified = false;
    this.changeCallback = typeof changeCallback === 'function' ? changeCallback : null;
    this.errorCallback = typeof errorHandler === 'function' ? errorHandler : null;
    this.trackDebugStatus(
      'peer.init',
      'call.audioCall.debugStatus.initializingPeer',
      'neutral',
      'call.audioCall.debugEvents.peer',
      {
        partnerId,
        orderId,
        isCustomer,
        isShared,
      },
    );

    this.addMediaElementListeners();
    this.createPeerConnection();
    const peer: RTCPeerConnection = this.peerConnection;
    const ready: Promise<void> = this.initMedia(peer);
    this.mediaInitialization = {peer, ready};
    try {
      await ready;
    } finally {
      if (this.mediaInitialization?.peer === peer) this.mediaInitialization = null;
    }
    if (!this.isCurrentPeer(peer)) return;
    await this.initConnection();
    if (!this.isCurrentPeer(peer)) return;
    await this.initializeAndroidAudioInput(peer);
  }

  public attachPlaybackElement(mediaElement: HTMLMediaElement): void {
    if (this.mediaElement !== mediaElement) {
      this.detachCurrentPlaybackElement();
      this.mediaElement = mediaElement;
      this.addMediaElementListeners();
    }

    if (this.syncPlaybackElementStream()) {
      void this.tryToPlay();
    }
  }

  public async warmUpAndroidAudioLabelsIfNeeded(): Promise<void> {
    if (!this.platformService.isAndroidApp) return;
    if (this.platformService.isSSR) return;
    if (typeof navigator === 'undefined') return;
    if (!navigator.mediaDevices?.getUserMedia) return;

    if (this.hasWarmedUpAndroidAudioLabels) return;
    if (this.warmUpAndroidAudioLabelsPromise) {
      await this.warmUpAndroidAudioLabelsPromise;
      return;
    }

    this.warmUpAndroidAudioLabelsPromise = this.doWarmUpAndroidAudioLabels();
    try {
      await this.warmUpAndroidAudioLabelsPromise;
    } finally {
      this.warmUpAndroidAudioLabelsPromise = null;
    }
  }

  private async doWarmUpAndroidAudioLabels(): Promise<void> {
    const hasLiveAudioTrack: boolean = this.localStream?.getAudioTracks().some((track: MediaStreamTrack): boolean => {
      return track.readyState === 'live';
    }) === true;

    if (hasLiveAudioTrack) {
      this.hasWarmedUpAndroidAudioLabels = true;
      return;
    }

    const mediaDevices: MediaDevices | undefined = navigator.mediaDevices;
    if (!mediaDevices?.getUserMedia) return;

    let stream: MediaStream | null = null;
    try {
      // Chrome/Android may return empty device labels until at least one getUserMedia() call happens in this app session.
      stream = await mediaDevices.getUserMedia(getAudioConstraints());
      if (mediaDevices.enumerateDevices) {
        await mediaDevices.enumerateDevices();
      }
      this.hasWarmedUpAndroidAudioLabels = true;
    } catch {
      // Ignore warm-up errors; routing will fall back to default behavior.
    } finally {
      stream?.getTracks().forEach((track: MediaStreamTrack): void => track.stop());
    }
  }

  private async initializeAndroidAudioInput(peer: RTCPeerConnection): Promise<void> {
    if (!this.platformService.isAndroidApp) return;
    if (this.platformService.isSSR) return;

    try {
      const inputDevices: MediaDeviceInfo[] = await this.listAudioInputDevices();
      if (!this.isCurrentPeer(peer)) return;
      if (inputDevices.length === 0) {
        console.error('No audio input devices found');
        return;
      }

      const currentDeviceId: string | null = this.getCurrentAudioInputDeviceId();
      const preferredDeviceId: string | null = this.resolvePreferredAndroidInputDeviceId(inputDevices);
      if (!preferredDeviceId) {
        this.syncSpeakerFlagByDeviceId(currentDeviceId, inputDevices);
        return;
      }

      if (currentDeviceId === preferredDeviceId) {
        this.syncSpeakerFlagByDeviceId(currentDeviceId, inputDevices);
        return;
      }

      await this.setInputDevice(preferredDeviceId);
      if (!this.isCurrentPeer(peer)) return;
      this.syncSpeakerFlagByDeviceId(preferredDeviceId, inputDevices);
    } catch (error) {
      console.error('Error enumerating devices:', error);
    }
  }

  private detectAudioRouteTypeFromLabel(label: string | null | undefined): 'bluetooth' | 'headphones' | 'earpiece' | 'speaker' | null {
    const normalized: string = (label ?? '').toLowerCase();

    if (!normalized) return null;
    const hasBluetoothToken: boolean = normalized.includes('bluetooth') || normalized.includes('a2dp') || normalized.includes('sco');
    const hasSpeakerToken: boolean = normalized.includes('speaker') || normalized.includes('loudspeaker')
      || normalized.includes('speakerphone') || normalized.includes('динам') || normalized.includes('громк');
    const hasEarpieceToken: boolean = normalized.includes('earpiece') || normalized.includes('receiver')
      || normalized.includes('communication') || normalized.includes('разговор')
      || normalized.includes('handset') || normalized.includes('phone earpiece');
    const hasHeadphonesToken: boolean = normalized.includes('headset') || normalized.includes('headphone')
      || normalized.includes('гарнитур') || normalized.includes('науш')
      || normalized.includes('earbud') || normalized.includes('airpods');

    if (hasBluetoothToken) return 'bluetooth';
    if (hasSpeakerToken) return 'speaker';
    if (hasEarpieceToken) return 'earpiece';
    if (hasHeadphonesToken) return 'headphones';

    return null;
  }

  private resolvePreferredAndroidInputDeviceId(devices: MediaDeviceInfo[]): string | null {
    const priority: ('bluetooth' | 'earpiece' | 'headphones')[] = ['bluetooth', 'earpiece', 'headphones'];

    for (const routeType of priority) {
      const matchedDevice: MediaDeviceInfo | undefined = devices.find((device: MediaDeviceInfo): boolean => {
        return this.detectAudioRouteTypeFromLabel(device.label) === routeType;
      });
      if (matchedDevice) {
        return matchedDevice.deviceId;
      }
    }

    const unknownRouteDevice: MediaDeviceInfo | undefined = devices.find((device: MediaDeviceInfo): boolean => {
      return this.detectAudioRouteTypeFromLabel(device.label) === null;
    });
    if (unknownRouteDevice) {
      return unknownRouteDevice.deviceId;
    }

    const speakerDevice: MediaDeviceInfo | undefined = devices.find((device: MediaDeviceInfo): boolean => {
      return this.detectAudioRouteTypeFromLabel(device.label) === 'speaker';
    });
    return speakerDevice?.deviceId ?? null;
  }

  private syncSpeakerFlagByDeviceId(deviceId: string | null, devices: MediaDeviceInfo[]): void {
    if (!deviceId) return;

    const device: MediaDeviceInfo | undefined = devices.find((item: MediaDeviceInfo): boolean => item.deviceId === deviceId);
    const routeType: 'bluetooth' | 'headphones' | 'earpiece' | 'speaker' | null =
      this.detectAudioRouteTypeFromLabel(device?.label);

    if (routeType === 'speaker') {
      this.withSpeaker = true;
    } else if (routeType === 'bluetooth' || routeType === 'headphones' || routeType === 'earpiece') {
      this.withSpeaker = false;
    } else if (devices.length === 2) {
      // On most phones two routes mean earpiece + speaker, speaker must stay opt-in.
      this.withSpeaker = false;
    }
  }

  private async initMedia(peer: RTCPeerConnection): Promise<void> {
    try {
      this.trackDebugStatus(
        'media.init',
        'call.audioCall.debugStatus.initializingMedia',
        'neutral',
        'call.audioCall.debugEvents.media',
        {
          orderId: this.orderId,
          partnerId: this.partnerId,
        },
      );
      await this.initMediaStream(peer);
      if (!this.isCurrentPeer(peer)) return;
      await this.addLocalTracksToPeerConnection(peer);
      if (!this.isCurrentPeer(peer)) return;
      this.trackDebugStatus(
        'media.ready',
        'call.audioCall.debugStatus.mediaReady',
        'success',
        'call.audioCall.debugEvents.media',
        {
          orderId: this.orderId,
          partnerId: this.partnerId,
        },
      );
    } catch (error) {
      if (!this.isCurrentPeer(peer)) return;
      console.error(error);
      this.trackDebugStatus(
        'media.failed',
        'call.audioCall.debugStatus.failed',
        'error',
        'call.audioCall.debugEvents.media',
        error,
      );
      throw error;
    }
  }

  private async initMediaStream(peer: RTCPeerConnection): Promise<void> {
    if (typeof navigator === 'undefined') return;

    const hasLiveAudioTrack: boolean = this.localStream?.getAudioTracks().some((track: MediaStreamTrack): boolean => {
      return track.readyState === 'live';
    }) === true;

    // If an audio stream was already prepared (e.g. to enforce initial route on Android),
    // reuse it to avoid accidental route switch on a second getUserMedia() call.
    if (hasLiveAudioTrack) {
      if (!this.mediaElement) {
        throw new Error('Media element is not defined');
      }
      return;
    }

    const constraints: MediaStreamConstraints = getAudioConstraints();
    const stream: MediaStream = await navigator.mediaDevices.getUserMedia(constraints);
    if (!this.isCurrentPeer(peer)) {
      stream.getTracks().forEach((track: MediaStreamTrack): void => track.stop());
      return;
    }
    this.localStream = stream;

    if (!this.localStream) {
      throw new Error('Media stream is not defined');
    }
    if (!this.mediaElement) {
      throw new Error('Media element is not defined');
    }
  }

  private async initConnection(): Promise<void> {
    if (!this.isExecutor) return;
    const peer: RTCPeerConnection | null = this.peerConnection;
    const delivered: boolean = await this.sendOffer();
    if (!this.isCurrentPeer(peer) || delivered) return;

    // The initial receipt may be lost after SDP/ICE succeeded. Recovery now belongs to reconnect.
    if (this.streamStartTime > 0) {
      this.trackDebugStatus(
        'offer.initial_receipt_unconfirmed',
        'call.audioCall.debugStatus.failed',
        'warning',
        'call.audioCall.debugEvents.offer',
        {order_id: this.orderId, connection_state: peer.connectionState},
        false,
      );
      return;
    }

    throw new Error('[WebrtcAudioService] Offer was not delivered (init_connection)');
  }

  private detachCurrentPlaybackElement(): void {
    this.clearMediaElementListeners();

    if (!this.mediaElement) return;

    this.mediaElement.pause();
    this.mediaElement.srcObject = null;
  }

  private syncPlaybackElementStream(): boolean {
    if (!this.mediaElement) return false;

    const playbackStream: MediaStream | null = this.remotePlaybackStream ?? this.remoteStream;
    if (!playbackStream) return false;
    if (this.mediaElement.srcObject === playbackStream) return true;

    this.mediaElement.srcObject = playbackStream;
    return true;
  }

  public stopPeerConnection(): void {
    this.statsSampler.stop();
    this.mediaInitialization = null;
    this.localOfferOperation = null;
    this.clearReconnectTimer();
    this.lastReconnectAt = 0;
    this.reconnectStartedAt = 0;
    this.reconnectAttempts = 0;
    this.reconnectFailedNotified = false;
    this.processedReliableMessages.clear();
    this.processingReliableMessages.clear();
    this.reliableAudioCallEventQueue = Promise.resolve();
    this.isConnected = false;
    this.partnerId = null;
    this.orderId = null;
    this.callSessionAtPeerStart = null;
    this.isCustomer = false;
    this.isShared = false;
    this.stopMedia();
    this.deletePeerConnection();
    this.streamStartTime = 0;
    this.localVideoTrack = null;
    this.setLocalPreviewStream(null);
    this.localVideoPictureInPictureStream = null;
    this.localVideoSender = null;
    this.remotePlaybackStream = null;
    this.remoteStream = null;
    this.remoteStreamSubject.next(null);
    this.resetRemoteVideoActivity();
    this.iosNativeVideoCallPictureInPictureAudioMode = false;
    this.videoCallCameraFacing = 'user';
    this.resetAudioProcessing();
    this.isEarpieceSimulated = true;
    this.withSpeaker = false;
    this.isVideoToggleInProgress = false;
    this.emitDebugState();
  }

  public stopReconnectAttempts(): void {
    this.reconnectFailedNotified = true;
    this.reconnectAttempts = this.maxReconnectAttempts;
    this.reconnectStartedAt = 0;
    this.clearReconnectTimer();
    this.emitDebugState();
  }

  private resetAudioProcessing(): void {
    if (this.audioContext) {
      if (this.sourceNode) {
        this.sourceNode.disconnect();
        this.sourceNode = null;
      }
      if (this.leftSpeakerGainNode) {
        this.leftSpeakerGainNode.disconnect();
        this.leftSpeakerGainNode = null;
      }
      if (this.rightSpeakerGainNode) {
        this.rightSpeakerGainNode.disconnect();
        this.rightSpeakerGainNode = null;
      }
      if (this.stereoMergerNode) {
        this.stereoMergerNode.disconnect();
        this.stereoMergerNode = null;
      }
      if (this.destinationNode) {
        this.destinationNode.disconnect();
        this.destinationNode = null;
      }
      this.audioProcessingSourceStream = null;
      this.audioContext.close();
      this.audioContext = null;
    }
  }

  private createPeerConnection(): void {
    if (this.peerConnection) {
      console.error('PeerConnection already initialized!');
      return;
    }

    const configuration: RTCConfiguration = this.buildRtcConfiguration();
    this.peerConnection = new RTCPeerConnection(configuration);
    const peer = this.peerConnection;
    this.ngZone.runOutsideAngular((): void => this.statsSampler.start(peer, (stage, metrics): void => {
      if (peer !== this.peerConnection) return;
      this.audioCallConnectionDebugService.milestone(stage, metrics);
    }));
    this.trackDebugStatus(
      'peer.created',
      'call.audioCall.debugStatus.peerReady',
      'success',
      'call.audioCall.debugEvents.peer',
      {
        orderId: this.orderId,
        partnerId: this.partnerId,
      },
    );

    this.subscribePeerConnectionEvents();
    this.subscribeAudioCallEvents();
    this.emitDebugState();
  }

  private deletePeerConnection(): void {
    this.showButtonPlaySubject.next(false);

    if (this.audioCallEventSubscription) {
      this.audioCallEventSubscription.unsubscribe();
      this.audioCallEventSubscription = null;
    }

    this.iceCandidatesBuffer = [];

    if (!this.peerConnection) return;

    this.peerConnection.close();
    this.unsubscribePeerConnectionEvents();
    this.peerConnection = null;
    this.emitDebugState();
  }

  private subscribePeerConnectionEvents(): void {
    if (!this.isShared) {
      this.peerConnection.onicecandidate = (event: RTCPeerConnectionIceEvent): void => this.onIceCandidate(event);
      this.peerConnection.oniceconnectionstatechange = (event: Event): void => this.onIceConnectionStateChange(event);
      this.peerConnection.onicegatheringstatechange = (event: Event): void => this.onIceGatheringStateChange(event);
      this.peerConnection.onicecandidateerror = (event: RTCPeerConnectionIceErrorEvent): void => this.onIceCandidateError(event);
    }
    this.peerConnection.onconnectionstatechange = (event: Event): Promise<void> => this.onConnectionStateChange(event);
    this.peerConnection.onnegotiationneeded = (event: Event): Promise<void> => this.onNegotiationNeeded(event);
    this.peerConnection.onsignalingstatechange = (event: Event): void => this.onSignalingStateChange(event);
    this.peerConnection.ontrack = (event: RTCTrackEvent): void => this.onTrack(event);
  }

  private unsubscribePeerConnectionEvents(): void {
    if (!this.isShared) {
      this.peerConnection.onicecandidate = null;
      this.peerConnection.oniceconnectionstatechange = null;
      this.peerConnection.onicegatheringstatechange = null;
      this.peerConnection.onicecandidateerror = null;
    }
    this.peerConnection.onconnectionstatechange = null;
    this.peerConnection.onnegotiationneeded = null;
    this.peerConnection.onsignalingstatechange = null;
    this.peerConnection.ontrack = null;
  }

  private onIceCandidate(event: RTCPeerConnectionIceEvent): void {
    if (!event?.candidate || !this.isValidCandidate(event?.candidate)) return;
    if (!this.partnerId) return;

    if (this.allCandidates.local.find(candidate => candidate.candidate === event.candidate?.candidate)) return;

    const candidateData: RTCIceCandidateInit = event.candidate.toJSON();
    this.trackDebugStatus(
      'candidate.local',
      'call.audioCall.debugStatus.sending',
      'neutral',
      'call.audioCall.debugEvents.candidate',
      {
        orderId: this.orderId,
        partnerId: this.partnerId,
      },
      false,
    );
    this.webSocketController.emitAudioCallCandidate(this.partnerId, candidateData, this.orderId ?? undefined);
    this.allCandidates.local.push(event.candidate);

  }

  private onIceConnectionStateChange(event: Event): void {
    if (!this.peerConnection || event.target !== this.peerConnection) return;
    this.audioCallConnectionDebugService.milestone('ice.state', {iceState: this.peerConnection.iceConnectionState});
    switch (this.peerConnection.iceConnectionState) {
      case 'connected':
      case 'completed':
        this.clearReconnectTimer();
        break;
      case 'disconnected':
        this.scheduleReconnect();
        break;
      case 'failed':
        this.triggerReconnect();
        break;
      case 'closed':
        this.clearReconnectTimer();
        break;
      default:
        break;
    }
    this.emitDebugState();
  }

  private onIceGatheringStateChange(event: Event): void {
    this.emitDebugState();
  }

  private onIceCandidateError(event: RTCPeerConnectionIceErrorEvent): void {
    if (event.target !== this.peerConnection) return;
    this.audioCallConnectionDebugService.milestone('ice.candidate_error', {ice_error_code: event.errorCode}, 'warning');
  }

  private async onConnectionStateChange(event: Event): Promise<void> {
    const peer: RTCPeerConnection | null = this.peerConnection;
    if (!peer || event.target !== peer) return;
    await this.ngZone.run(async (): Promise<void> => {
      if (!this.isCurrentPeer(peer)) return;
      switch (this.peerConnection.connectionState) {
        case 'connecting':
          this.trackDebugStatus(
            'peer.connecting',
            'call.audioCall.debugStatus.connecting',
            'neutral',
            'call.audioCall.debugEvents.peer',
            {
              connectionState: this.peerConnection.connectionState,
              iceState: this.peerConnection.iceConnectionState,
            },
          );
          if (this.streamStartTime <= 0 && !this.isConnected) {
            this.audioService.playConnectingSound();
          }
          break;
        case 'connected':
          // Connection ownership is established before optional native haptics can delay the UI.
          this.callSessionService.recordEstablishedMedia(this.callSessionAtPeerStart);
          this.audioService.stopSound();
          console.log('Peer connection is connected');
          void this.createVibration(NotificationType.Success).catch(() => undefined);

          if (!this.streamStartTime) {
            this.streamStartTime = Date.now();
          }

          this.isConnected = true;
          this.reconnectAttempts = 0;
          this.reconnectStartedAt = 0;
          this.lastReconnectAt = 0;
          this.reconnectFailedNotified = false;
          this.clearReconnectTimer();

          if (!this.isShared) {
            this.webSocketController.audioCallWebrtcStatus(this.orderId, this.isConnected);
          }

          this.trackDebugStatus(
            'peer.connected',
            'call.audioCall.debugStatus.connected',
            'success',
            'call.audioCall.debugEvents.peer',
            {
              connectionState: this.peerConnection.connectionState,
              iceState: this.peerConnection.iceConnectionState,
            },
          );

          await this.tryToPlay();
          if (!this.isCurrentPeer(peer)) return;

          if (typeof this.changeCallback === 'function') this.changeCallback();
          break;
        case 'disconnected':
          this.audioService.playDisconnectedSound();

          console.log('Peer connection is disconnected');
          this.isConnected = false;
          this.trackDebugStatus(
            'peer.disconnected',
            'call.audioCall.debugStatus.disconnected',
            'warning',
            'call.audioCall.debugEvents.peer',
            {
              connectionState: this.peerConnection.connectionState,
              iceState: this.peerConnection.iceConnectionState,
            },
          );
          this.scheduleReconnect();
          break;
        case 'failed':
          console.log('Peer connection is failed');
          this.isConnected = false;
          this.trackDebugStatus(
            'peer.failed',
            'call.audioCall.debugStatus.failed',
            'error',
            'call.audioCall.debugEvents.peer',
            {
              connectionState: this.peerConnection.connectionState,
              iceState: this.peerConnection.iceConnectionState,
            },
          );

          if (!this.isShared) {
            this.webSocketController.audioCallWebrtcStatus(this.orderId, this.isConnected);
          }
          this.triggerReconnect();
          break;
        case 'closed':
          this.audioService.stopSound();
          console.log('Peer connection closed');
          this.trackDebugStatus(
            'peer.closed',
            'call.audioCall.debugStatus.closed',
            'neutral',
            'call.audioCall.debugEvents.peer',
            {
              connectionState: this.peerConnection.connectionState,
              iceState: this.peerConnection.iceConnectionState,
            },
          );
          break;
      }
      this.emitDebugState();
    });
  }

  protected async onNegotiationNeeded(event: Event): Promise<void> {
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  private scheduleReconnect(): void {
    if (!this.shouldInitiateReconnect()) return;
    this.ensureReconnectStarted();
    if (this.hasReconnectDeadlineExpired()) {
      this.notifyReconnectFailed();
      return;
    }
    if (this.reconnectTimeout) return;

    this.reconnectTimeout = setTimeout((): void => {
      this.reconnectTimeout = null;
      this.triggerReconnect();
    }, this.reconnectDelayMs);
  }

  private triggerReconnect(): void {
    if (!this.shouldInitiateReconnect()) return;
    this.ensureReconnectStarted();

    this.clearReconnectTimer();

    const now: number = Date.now();
    if (this.hasReconnectDeadlineExpired(now)) {
      this.notifyReconnectFailed();
      return;
    }

    const sinceLastAttempt: number = now - this.lastReconnectAt;
    if (sinceLastAttempt < this.reconnectCooldownMs) {
      const delay: number = this.reconnectCooldownMs - sinceLastAttempt;
      if (!this.reconnectTimeout) {
        this.reconnectTimeout = setTimeout((): void => {
          this.reconnectTimeout = null;
          this.triggerReconnect();
        }, delay);
      }
      return;
    }
    this.lastReconnectAt = now;

    if (this.peerConnection?.connectionState === 'connecting' || this.peerConnection?.iceConnectionState === 'checking') {
      this.scheduleReconnect();
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.notifyReconnectFailed();
      return;
    }

    const isSocketReady: boolean = this.webSocketController.socketConnected
      && this.webSocketController.isSocketInitedSubject.value;
    if (!isSocketReady) {
      this.scheduleReconnect();
      return;
    }

    this.reconnectAttempts += 1;
    this.emitDebugState();
    this.trackDebugStatus(
      'reconnect.request',
      'call.audioCall.debugStatus.reconnectRequested',
      'warning',
      'call.audioCall.debugEvents.requestReconnect',
      {
        reconnectAttempts: this.reconnectAttempts,
        maxReconnectAttempts: this.maxReconnectAttempts,
        orderId: this.orderId,
        partnerId: this.partnerId,
      },
    );

    if (this.peerConnection?.iceGatheringState === 'gathering') {
      void this.peerConnection.addIceCandidate(null);
    }

    try {
      this.peerConnection?.restartIce();
    } catch (error) {
      console.warn('[WebrtcAudioService] ICE restart failed', error);
    }
    this.requestReconnect(this.partnerId, this.orderId);

    const connectionState: RTCPeerConnectionState | undefined = this.peerConnection?.connectionState;
    const iceState: RTCIceConnectionState | undefined = this.peerConnection?.iceConnectionState;
    if (connectionState !== 'connected'
      && connectionState !== 'closed'
      && iceState !== 'connected'
      && iceState !== 'completed'
      && iceState !== 'closed') {
      this.scheduleReconnect();
    }
  }

  private shouldInitiateReconnect(): boolean {
    if (!this.isExecutor) return false;
    if (this.isShared) return false;
    if (!this.partnerId || !this.orderId) return false;
    if (!this.peerConnection) return false;
    if (this.reconnectFailedNotified) return false;
    if (this.peerConnection.signalingState === 'closed') return false;
    if (this.peerConnection.connectionState === 'closed') return false;
    return true;
  }

  private ensureReconnectStarted(): void {
    if (this.reconnectStartedAt > 0) return;
    this.reconnectStartedAt = Date.now();
  }

  private hasReconnectDeadlineExpired(now: number = Date.now()): boolean {
    return this.reconnectStartedAt > 0
      && (now - this.reconnectStartedAt) >= this.reconnectDeadlineMs;
  }

  private notifyReconnectFailed(): void {
    this.clearReconnectTimer();
    if (this.reconnectFailedNotified) return;

    this.reconnectFailedNotified = true;
    this.emitDebugState();
    this.reconnectFailedSubject.next();
  }

  private cleanupProcessedReliableMessages(): void {
    const now: number = Date.now();
    this.processedReliableMessages.forEach((expiresAt: number, messageId: string): void => {
      if (expiresAt > now) return;
      this.processedReliableMessages.delete(messageId);
    });
  }

  private isProcessedReliableMessage(messageId: string): boolean {
    this.cleanupProcessedReliableMessages();
    const expiresAt: number | undefined = this.processedReliableMessages.get(messageId);
    if (!expiresAt) return false;
    if (expiresAt <= Date.now()) {
      this.processedReliableMessages.delete(messageId);
      return false;
    }
    return true;
  }

  private markReliableMessageProcessed(messageId: string): void {
    this.cleanupProcessedReliableMessages();
    this.processedReliableMessages.set(messageId, Date.now() + this.processedReliableMessageTtlMs);
  }

  private getReliableMessageInProcessing(messageId: string): Promise<ReliableAudioCallProcessingResult> | null {
    return this.processingReliableMessages.get(messageId) ?? null;
  }

  private markReliableMessageProcessing(messageId: string, processingPromise: Promise<ReliableAudioCallProcessingResult>): void {
    this.processingReliableMessages.set(messageId, processingPromise);
  }

  private unmarkReliableMessageProcessing(messageId: string): void {
    this.processingReliableMessages.delete(messageId);
  }

  private normalizeReliableMessageId(meta: AudioCallReliableMeta | null | undefined): string | null {
    if (!meta) return null;
    if (typeof meta.message_id !== 'string') return null;
    const normalized: string = meta.message_id.trim();
    if (normalized.length === 0) return null;
    return normalized;
  }

  private resolveReliableErrorMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === 'string' && error.trim().length > 0) {
      return error.trim();
    }
    return 'Event processing failed';
  }

  private async processReliableAudioCallEvent(
    meta: AudioCallReliableMeta | null,
    processEvent: () => Promise<AudioCallDeliveryStatus>,
  ): Promise<void> {
    const peer: RTCPeerConnection | null = this.peerConnection;
    const messageId: string | null = this.normalizeReliableMessageId(meta);
    if (!meta || !messageId) {
      await processEvent();
      return;
    }

    if (this.isProcessedReliableMessage(messageId)) {
      this.webSocketController.emitAudioCallDeliveryReceipt(meta, 'duplicate');
      return;
    }

    const activeProcessing: Promise<ReliableAudioCallProcessingResult> | null = this.getReliableMessageInProcessing(messageId);
    if (activeProcessing) {
      const activeResult: ReliableAudioCallProcessingResult = await activeProcessing;
      if (this.peerConnection !== peer) return;
      const duplicateStatus: AudioCallDeliveryStatus = activeResult.status === 'processed'
        ? 'duplicate'
        : activeResult.status;
      this.webSocketController.emitAudioCallDeliveryReceipt(meta, duplicateStatus, activeResult.errorMessage);
      return;
    }

    const processingPromise: Promise<ReliableAudioCallProcessingResult> = (async (): Promise<ReliableAudioCallProcessingResult> => {
      let status: AudioCallDeliveryStatus = 'ignored';
      let errorMessage: string | undefined;
      let processingError: unknown;

      try {
        status = await processEvent();
        if (this.peerConnection !== peer) {
          status = 'ignored';
        } else if (status === 'processed') {
          this.markReliableMessageProcessed(messageId);
        } else {
          this.processedReliableMessages.delete(messageId);
        }
      } catch (error) {
        status = 'failed';
        errorMessage = this.resolveReliableErrorMessage(error);
        processingError = error;
      } finally {
        if (this.peerConnection === peer) this.unmarkReliableMessageProcessing(messageId);
      }

      return {
        status,
        errorMessage,
        error: processingError,
      };
    })();

    this.markReliableMessageProcessing(messageId, processingPromise);
    const processingResult: ReliableAudioCallProcessingResult = await processingPromise;
    if (this.peerConnection !== peer) return;
    this.webSocketController.emitAudioCallDeliveryReceipt(meta, processingResult.status, processingResult.errorMessage);

    if (processingResult.error) {
      throw processingResult.error;
    }
  }

  private onTrack(event: RTCTrackEvent): void {
    if (!this.remoteStream) {
      this.remoteStream = event.streams?.[0] ?? new MediaStream();
    }

    const sourceStream: MediaStream | undefined = event.streams?.[0];
    this.attachRemoteSourceStream(sourceStream);

    const remoteStream: MediaStream = this.remoteStream;
    this.syncRemoteTracks(remoteStream, sourceStream);

    if (!remoteStream.getTracks().some((track: MediaStreamTrack): boolean => track.id === event.track.id)) {
      try {
        remoteStream.addTrack(event.track);
      } catch (error) {
        console.warn('Failed to add track to remote stream:', error);
      }
    }
    remoteStream.getTracks().forEach((track: MediaStreamTrack): void => this.attachRemoteTrackListeners(track));

    this.remoteStream = remoteStream;
    this.remoteStreamSubject.next(remoteStream);
    this.syncRemoteVideoActivity();

    if (!this.mediaElement) return;

    const playbackStream: MediaStream = this.resolvePlaybackStream(remoteStream, event);

    if (this.mediaElement.srcObject !== playbackStream) {
      this.mediaElement.srcObject = playbackStream;
    }

    void this.tryToPlay();

    if (typeof this.changeCallback === 'function') this.changeCallback();
  }

  private isActiveRemoteVideoTrack(track: MediaStreamTrack): boolean {
    if (track.readyState !== 'live') return false;
    return track.muted !== true;
  }

  private syncRemoteTracks(remoteStream: MediaStream, sourceStream: MediaStream | undefined): void {
    if (!sourceStream) return;
    if (sourceStream !== remoteStream) return;

    const sourceTracks: MediaStreamTrack[] = sourceStream.getTracks();
    if (sourceTracks.length === 0) return;

    const sourceTrackIds: Set<string> = new Set(sourceTracks.map((track: MediaStreamTrack): string => track.id));
    const sourceKinds: Set<string> = new Set(sourceTracks.map((track: MediaStreamTrack): string => track.kind));
    const staleTracks: MediaStreamTrack[] = remoteStream.getTracks().filter((track: MediaStreamTrack): boolean => {
      return sourceKinds.has(track.kind) && !sourceTrackIds.has(track.id);
    });

    for (const staleTrack of staleTracks) {
      remoteStream.removeTrack(staleTrack);
      this.detachRemoteTrackListeners(staleTrack);

      if (this.remotePlaybackStream) {
        const stalePlaybackTrack: MediaStreamTrack | undefined = this.remotePlaybackStream.getTracks().find((track: MediaStreamTrack): boolean => track.id === staleTrack.id);
        if (stalePlaybackTrack) {
          this.remotePlaybackStream.removeTrack(stalePlaybackTrack);
        }
      }
    }

    // Do not nullify remote stream here: this callback can be triggered
    // before a fresh track is added in the same onTrack cycle.
  }

  private attachRemoteSourceStream(stream: MediaStream | null | undefined): void {
    if (!stream || this.remoteSourceStream === stream) return;

    this.detachRemoteSourceStream();
    this.remoteSourceStream = stream;
    this.remoteSourceStream.addEventListener('removetrack', this.onRemoteSourceRemoveTrack);
  }

  private detachRemoteSourceStream(): void {
    if (!this.remoteSourceStream) return;

    this.remoteSourceStream.removeEventListener('removetrack', this.onRemoteSourceRemoveTrack);
    this.remoteSourceStream = null;
  }

  private handleRemoteTrackStateChanged(): void {
    this.remoteStreamSubject.next(this.remoteStream);
    this.syncRemoteVideoActivity();
    if (typeof this.changeCallback === 'function') this.changeCallback();
  }

  private syncRemoteVideoActivity(): void {
    const hasActiveRemoteVideo: boolean = this.hasRemoteVideoTrack();
    if (hasActiveRemoteVideo) {
      this.remoteVideoWasActiveForCurrentConnection = true;
    }

    if (this.remoteVideoActiveSubject.value === hasActiveRemoteVideo) return;
    this.remoteVideoActiveSubject.next(hasActiveRemoteVideo);
  }

  private resetRemoteVideoActivity(): void {
    this.remoteVideoWasActiveForCurrentConnection = false;
    if (!this.remoteVideoActiveSubject.value) return;
    this.remoteVideoActiveSubject.next(false);
  }

  private attachRemoteTrackListeners(track: MediaStreamTrack): void {
    if (this.remoteTrackListeners.has(track)) return;

    const onMuteChanged: () => void = (): void => this.handleRemoteTrackStateChanged();
    const onEnded: () => void = (): void => this.removeRemoteTrack(track);

    track.addEventListener('mute', onMuteChanged);
    track.addEventListener('unmute', onMuteChanged);
    track.addEventListener('ended', onEnded);

    this.remoteTrackListeners.set(track, (): void => {
      track.removeEventListener('mute', onMuteChanged);
      track.removeEventListener('unmute', onMuteChanged);
      track.removeEventListener('ended', onEnded);
    });
  }

  private detachRemoteTrackListeners(track: MediaStreamTrack): void {
    const removeListeners: (() => void) | undefined = this.remoteTrackListeners.get(track);
    if (!removeListeners) return;

    removeListeners();
    this.remoteTrackListeners.delete(track);
  }

  private clearRemoteTrackListeners(): void {
    this.remoteTrackListeners.forEach((removeListeners: () => void): void => removeListeners());
    this.remoteTrackListeners.clear();
  }

  private onSignalingStateChange(event: Event): void {
    this.emitDebugState();
  }

  private emitDebugState(): void {
    this.debugStateSubject.next(this.buildDebugStateSnapshot());
  }

  private buildDebugStateSnapshot(): AudioCallWebrtcDebugState {
    return {
      hasPeerConnection: !!this.peerConnection,
      isConnected: this.isConnected,
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts,
      connectionState: this.peerConnection?.connectionState ?? 'none',
      iceConnectionState: this.peerConnection?.iceConnectionState ?? 'none',
      signalingState: this.peerConnection?.signalingState ?? 'none',
      iceGatheringState: this.peerConnection?.iceGatheringState ?? 'none',
    };
  }

  private resolvePlaybackStream(remoteStream: MediaStream, event: RTCTrackEvent): MediaStream {
    if (this.shouldUseDirectIosNativeVideoCallPlayback()) {
      this.remotePlaybackStream = remoteStream;
      return remoteStream;
    }

    if (!this.shouldUseAudioProcessing(event.track)) {
      this.remotePlaybackStream = remoteStream;
      return remoteStream;
    }

    const sourceStream: MediaStream | null = this.resolveAudioProcessingSourceStream(remoteStream, event);
    if (!sourceStream) {
      this.remotePlaybackStream = remoteStream;
      return remoteStream;
    }

    this.ensureStereoPipeline(sourceStream);
    const playbackStream: MediaStream = this.buildProcessedPlaybackStream(remoteStream);
    this.remotePlaybackStream = playbackStream;
    return playbackStream;
  }

  private shouldUseAudioProcessing(track: MediaStreamTrack): boolean {
    if (!this.platformService.isIosApp) return false;
    if (this.shouldUseDirectIosNativeVideoCallPlayback()) return false;
    if (track.kind === 'audio') return true;
    return !!this.destinationNode;
  }

  private shouldUseDirectIosNativeVideoCallPlayback(): boolean {
    return this.platformService.isIosApp && this.iosNativeVideoCallPictureInPictureAudioMode;
  }

  private resolveAudioProcessingSourceStream(remoteStream: MediaStream, event: RTCTrackEvent): MediaStream | null {
    const eventStream: MediaStream | null =
      event.streams?.find((stream: MediaStream): boolean => this.hasLiveAudioTrack(stream)) ?? null;

    if (event.track.kind === 'audio' && eventStream) return eventStream;
    if (this.hasLiveAudioTrack(this.audioProcessingSourceStream)) return this.audioProcessingSourceStream;
    if (eventStream) return eventStream;
    if (this.hasLiveAudioTrack(remoteStream)) return remoteStream;

    return null;
  }

  private hasLiveAudioTrack(stream: MediaStream | null | undefined): stream is MediaStream {
    return !!stream?.getAudioTracks().some((track: MediaStreamTrack): boolean => track.readyState === 'live');
  }

  private ensureStereoPipeline(sourceStream: MediaStream): void {
    if (!this.audioContext) {
      const audioContextCtor: typeof AudioContext | undefined = window.AudioContext ?? window.webkitAudioContext;
      if (!audioContextCtor) return;
      this.audioContext = new audioContextCtor();
    }

    this.resumeAudioContextIfNeeded();

    const requiresSetup: boolean = !this.sourceNode
      || !this.leftSpeakerGainNode
      || !this.rightSpeakerGainNode
      || !this.stereoMergerNode
      || !this.destinationNode
      || this.audioProcessingSourceStream !== sourceStream;

    if (!requiresSetup || !this.audioContext) return;

    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }
    if (this.leftSpeakerGainNode) {
      this.leftSpeakerGainNode.disconnect();
      this.leftSpeakerGainNode = null;
    }
    if (this.rightSpeakerGainNode) {
      this.rightSpeakerGainNode.disconnect();
      this.rightSpeakerGainNode = null;
    }
    if (this.stereoMergerNode) {
      this.stereoMergerNode.disconnect();
      this.stereoMergerNode = null;
    }
    if (this.destinationNode) {
      this.destinationNode.disconnect();
      this.destinationNode = null;
    }

    const sourceNode: MediaStreamAudioSourceNode = this.audioContext.createMediaStreamSource(sourceStream);
    const leftGainNode: GainNode = this.audioContext.createGain();
    const rightGainNode: GainNode = this.audioContext.createGain();
    const stereoMergerNode: ChannelMergerNode = this.audioContext.createChannelMerger(2);
    const destinationNode: MediaStreamAudioDestinationNode = this.audioContext.createMediaStreamDestination();

    this.sourceNode = sourceNode;
    this.leftSpeakerGainNode = leftGainNode;
    this.rightSpeakerGainNode = rightGainNode;
    this.stereoMergerNode = stereoMergerNode;
    this.destinationNode = destinationNode;
    this.audioProcessingSourceStream = sourceStream;

    const leftLevel: number = this.isEarpieceSimulated ? this.earpieceLevels.left : 1.0;
    const rightLevel: number = this.isEarpieceSimulated ? this.earpieceLevels.right : 1.0;
    this.setStereoLevels(leftLevel, rightLevel);

    sourceNode.connect(leftGainNode);
    sourceNode.connect(rightGainNode);
    leftGainNode.connect(stereoMergerNode, 0, 0);
    rightGainNode.connect(stereoMergerNode, 0, 1);
    stereoMergerNode.connect(destinationNode);

    this.resumeAudioContextIfNeeded();
  }

  private setStereoLevels(leftVolume: number, rightVolume: number): void {
    if (this.leftSpeakerGainNode) {
      this.leftSpeakerGainNode.gain.value = leftVolume;
    }
    if (this.rightSpeakerGainNode) {
      this.rightSpeakerGainNode.gain.value = rightVolume;
    }
  }

  private buildProcessedPlaybackStream(remoteStream: MediaStream): MediaStream {
    const audioStream: MediaStream | null = this.destinationNode?.stream ?? null;

    if (!audioStream) {
      return remoteStream;
    }

    const playbackStream: MediaStream =
      this.remotePlaybackStream && this.remotePlaybackStream !== remoteStream
        ? this.remotePlaybackStream
        : new MediaStream();
    this.removeStaleTracks(playbackStream, audioStream, remoteStream);

    audioStream.getAudioTracks().forEach((audioTrack: MediaStreamTrack): void => {
      this.addTrackIfMissing(playbackStream, audioTrack);
    });

    remoteStream.getVideoTracks().forEach((videoTrack: MediaStreamTrack): void => {
      this.addTrackIfMissing(playbackStream, videoTrack);
    });

    return playbackStream;
  }

  private removeStaleTracks(playbackStream: MediaStream, audioStream: MediaStream, remoteStream: MediaStream): void {
    const existingTracks: MediaStreamTrack[] = [...playbackStream.getTracks()];

    existingTracks.forEach((existingTrack: MediaStreamTrack): void => {
      const audioStillPresent: boolean = audioStream.getAudioTracks().some((track: MediaStreamTrack): boolean => track.id === existingTrack.id);
      const videoStillPresent: boolean = remoteStream.getVideoTracks().some((track: MediaStreamTrack): boolean => track.id === existingTrack.id);

      if (!audioStillPresent && !videoStillPresent) {
        playbackStream.removeTrack(existingTrack);
      }
    });
  }

  private addTrackIfMissing(stream: MediaStream, track: MediaStreamTrack): void {
    if (stream.getTracks().some((existing: MediaStreamTrack): boolean => existing.id === track.id)) return;

    try {
      stream.addTrack(track);
    } catch (error) {
      console.warn('Failed to add track to playback stream:', error);
    }
  }

  private resumeAudioContextIfNeeded(): void {
    const audioContext: AudioContext | null = this.audioContext;
    if (!audioContext || audioContext.state === 'closed' || audioContext.state === 'running') return;

    audioContext.resume().catch((error: unknown): void => {
      console.error('Failed to resume audio context:', error);
      this.showButtonPlaySubject.next(true);
    });
  }

  private rebuildRemotePlaybackStream(): void {
    const remoteStream: MediaStream | null = this.remoteStream;
    if (!remoteStream) return;

    if (this.shouldUseDirectIosNativeVideoCallPlayback()) {
      this.remotePlaybackStream = remoteStream;
    } else {
      const sourceStream: MediaStream | null =
        this.hasLiveAudioTrack(this.audioProcessingSourceStream)
          ? this.audioProcessingSourceStream
          : remoteStream;
      if (this.hasLiveAudioTrack(sourceStream)) {
        this.ensureStereoPipeline(sourceStream);
        this.remotePlaybackStream = this.buildProcessedPlaybackStream(remoteStream);
      } else {
        this.remotePlaybackStream = remoteStream;
      }
    }

    if (this.syncPlaybackElementStream()) {
      void this.tryToPlay();
    }
  }

  public toggleMic(withAudio: boolean): void {
    this.localStream?.getAudioTracks().forEach((track: MediaStreamTrack): void => {
      track.enabled = withAudio;
    });
  }

  public toggleAudioMode(): void {
    if (!this.platformService.isIosApp || !this.audioContext || !this.leftSpeakerGainNode || !this.rightSpeakerGainNode || !this.mediaElement) {
      console.warn('Cannot toggle audio mode: iOS only and audio context required');
      return;
    }

    this.isEarpieceSimulated = this.isEarpieceSimulated != true;
    if (this.isEarpieceSimulated) {
      this.setStereoLevels(this.earpieceLevels.left, this.earpieceLevels.right);
    } else {
      this.setStereoLevels(1.0, 1.0);
    }

    this.mediaElement.play().catch((error: unknown): void => {
      console.error('Play failed after toggle:', error);
      this.showButtonPlaySubject.next(true);
    });
  }

  public enableIosSpeakerMode(): void {
    if (!this.platformService.isIosApp) return;
    if (!this.isEarpieceSimulated) return;

    this.isEarpieceSimulated = false;
    if (this.leftSpeakerGainNode && this.rightSpeakerGainNode) {
      this.setStereoLevels(1.0, 1.0);
    }

    if (!this.mediaElement) return;
    this.mediaElement.play().catch((error: unknown): void => {
      console.error('Play failed after forcing iOS speaker mode:', error);
      this.showButtonPlaySubject.next(true);
    });
  }

  public enableIosEarpieceMode(): void {
    if (!this.platformService.isIosApp) return;
    if (this.isEarpieceSimulated) return;

    this.isEarpieceSimulated = true;
    if (this.leftSpeakerGainNode && this.rightSpeakerGainNode) {
      this.setStereoLevels(this.earpieceLevels.left, this.earpieceLevels.right);
    }

    if (!this.mediaElement) return;
    this.mediaElement.play().catch((error: unknown): void => {
      console.error('Play failed after forcing iOS earpiece mode:', error);
      this.showButtonPlaySubject.next(true);
    });
  }

  public async listAudioInputDevices(): Promise<MediaDeviceInfo[]> {
    if (this.platformService.isSSR) return [];
    if (typeof navigator === 'undefined') return [];

    const mediaDevices: MediaDevices | undefined = navigator.mediaDevices;

    if (!mediaDevices || !mediaDevices.enumerateDevices) return [];

    const deviceInfos: MediaDeviceInfo[] = await mediaDevices.enumerateDevices();
    const inputDevices: MediaDeviceInfo[] = [];

    for (const deviceInfo of deviceInfos) {
      if (deviceInfo.kind === 'audioinput' && deviceInfo.deviceId !== '' && deviceInfo.deviceId !== 'default') {
        inputDevices.push(deviceInfo);
      }
    }

    return inputDevices;
  }

  public getCurrentAudioInputDeviceId(): string | null {
    if (!this.localStream) return null;

    const audioTrack: MediaStreamTrack | undefined = this.localStream.getAudioTracks()[0];
    if (!audioTrack) return null;

    const settings: MediaTrackSettings = audioTrack.getSettings();
    const deviceId: unknown = settings.deviceId;

    if (typeof deviceId === 'string' && deviceId.length > 0) {
      return deviceId;
    }

    return null;
  }

  public getCurrentAudioInputTrackLabel(): string | null {
    if (!this.localStream) return null;

    const audioTrack: MediaStreamTrack | undefined = this.localStream.getAudioTracks()[0];
    if (!audioTrack) return null;

    const label: string | null = audioTrack.label ?? null;
    if (!label) return null;

    const normalized: string = label.trim();
    return normalized.length > 0 ? normalized : null;
  }

  public async setInputDevice(deviceId: string): Promise<void> {
    if (this.platformService.isSSR) return;
    if (typeof deviceId !== 'string' || deviceId.length === 0) return;

    await this.replaceAudioTrack(deviceId);
  }

  public async toggleInputDevice(): Promise<void> {
    if (this.platformService.isSSR) return;
    const peer = this.peerConnection;
    if (!this.isCurrentPeer(peer)) return;
    this.withSpeaker = this.withSpeaker !== true;

    try {
      const devices: MediaDeviceInfo[] = await this.listAudioInputDevices();
      if (!this.isCurrentPeer(peer)) return;

      if (devices.length === 0) {
        console.error('No audio input devices found');
        return;
      }

      const deviceIds: string[] = devices.map((device: MediaDeviceInfo): string => device.deviceId);
      const currentDeviceId: string | null = this.getCurrentAudioInputDeviceId();
      let currentIndex: number = currentDeviceId ? deviceIds.findIndex((id: string): boolean => id === currentDeviceId) : -1;

      if (currentIndex === -1) {
        currentIndex = 0;
      }

      const nextIndex: number = (currentIndex + 1) % deviceIds.length;
      const nextDeviceId: string = deviceIds[nextIndex];

      await this.replaceAudioTrack(nextDeviceId);
    } catch (error) {
      console.error('Error enumerating devices:', error);
    }
  }

  private async replaceAudioTrack(nextDeviceId: string): Promise<void> {
    if (this.platformService.isSSR) return;
    const peer = this.peerConnection;
    if (!this.isCurrentPeer(peer)) return;

    try {
      const newTrack: MediaStreamTrack = await this.createReplacementAudioTrack(nextDeviceId, 'replace_audio_input', false);
      if (!this.isCurrentPeer(peer)) {
        this.stopTrackSafely(newTrack);
        return;
      }
      await this.replaceLocalAudioTrack(newTrack, 'replace_audio_input');
    } catch (error) {
      console.error('Error replacing microphone stream:', error);
    }
  }

  public async enableLocalVideo(): Promise<void> {
    if (this.isVideoToggleInProgress || this.hasUsableLocalVideoTrack()) return;
    const peer = this.peerConnection;
    if (!this.isCurrentPeer(peer)) throw new Error('Peer connection is not initialized!');
    if (typeof navigator === 'undefined') throw new Error('Navigator is not available.');

    this.isVideoToggleInProgress = true;
    const previousVideoTrack: MediaStreamTrack | null = this.getLiveLocalVideoTrack();
    let replacedVideoTrack: MediaStreamTrack | null = null;

    try {
      replacedVideoTrack = await this.replaceVideoCallTrack(true);
      if (!this.isCurrentPeer(peer)) return;
      await this.ensureOfferSentOrThrow('enable_local_video');
      this.stopTrackSafely(replacedVideoTrack);
    } catch (error) {
      if (!this.isCurrentPeer(peer)) return;
      await this.rollbackLocalVideoTrack(previousVideoTrack);
      if (!this.isCurrentPeer(peer)) return;
      console.error('Error enabling local video track:', error);
      throw error;
    } finally {
      if (this.isCurrentPeer(peer)) this.isVideoToggleInProgress = false;
      else this.stopTrackSafely(previousVideoTrack);
    }
  }

  public async restoreLocalVideoTrack(): Promise<void> {
    if (this.isVideoToggleInProgress || this.hasUsableLocalVideoTrack() || this.isScreenSharing) return;
    const peer = this.peerConnection;
    if (!this.isCurrentPeer(peer)) throw new Error('Peer connection is not initialized!');
    if (typeof navigator === 'undefined') throw new Error('Navigator is not available.');

    this.isVideoToggleInProgress = true;
    const previousVideoTrack: MediaStreamTrack | null = this.getLiveLocalVideoTrack();
    let replacedVideoTrack: MediaStreamTrack | null = null;

    try {
      replacedVideoTrack = await this.replaceVideoCallTrack(true);
      if (!this.isCurrentPeer(peer)) return;
      await this.ensureOfferSentOrThrow('restore_local_video');
      this.stopTrackSafely(replacedVideoTrack);
    } catch (error) {
      if (!this.isCurrentPeer(peer)) return;
      await this.rollbackLocalVideoTrack(previousVideoTrack);
      if (!this.isCurrentPeer(peer)) return;
      console.error('Error restoring local video track:', error);
      throw error;
    } finally {
      if (this.isCurrentPeer(peer)) this.isVideoToggleInProgress = false;
      else this.stopTrackSafely(previousVideoTrack);
    }
  }

  public async disableLocalVideo(skipSendOffer: boolean = false, skipScreenShareStop: boolean = false): Promise<void> {
    if (this.isVideoToggleInProgress) return;
    const peer = this.peerConnection;
    if (!this.isCurrentPeer(peer)) return;

    if (this.isScreenSharing && !skipScreenShareStop) {
      this.wasCameraEnabledBeforeScreenShare = false;
      this.preservedCameraTrack = null;
      this.preservedCameraStream = null;
      await this.stopScreenShare();
      return;
    }

    if (!this.peerConnection || !this.localVideoTrack) return;

    this.isVideoToggleInProgress = true;
    const previousVideoTrack: MediaStreamTrack = this.localVideoTrack;

    try {
      await this.detachCurrentLocalVideoTrack(false);
      if (!this.isCurrentPeer(peer)) return;

      if (!skipSendOffer) {
        await this.ensureOfferSentOrThrow('disable_local_video');
      }
      this.stopTrackSafely(previousVideoTrack);
    } catch (error) {
      if (!this.isCurrentPeer(peer)) return;
      await this.rollbackLocalVideoTrack(previousVideoTrack);
      if (!this.isCurrentPeer(peer)) return;
      console.error('Error disabling local video track:', error);
      throw error;
    } finally {
      if (this.isCurrentPeer(peer)) this.isVideoToggleInProgress = false;
      else this.stopTrackSafely(previousVideoTrack);
    }
  }

  private async detachCurrentLocalVideoTrack(stopTrack: boolean): Promise<MediaStreamTrack | null> {
    const peer = this.peerConnection;
    if (!this.isCurrentPeer(peer)) return null;
    const videoTrack: MediaStreamTrack | null = this.localVideoTrack;
    if (!videoTrack) return null;

    await this.deactivateIosBackgroundCameraOutgoingVideoTrack();
    if (!this.isCurrentPeer(peer)) return null;

    if (this.localVideoSender) {
      await this.localVideoSender.replaceTrack(null);
      if (!this.isCurrentPeer(peer)) return null;
      this.disableVideoTransceiverSending(this.localVideoSender);

      try {
        this.localVideoSender.setStreams();
      } catch (error) {
        console.warn('Unable to clear sender streams:', error);
      }
    }

    try {
      if (this.localStream?.getVideoTracks().includes(videoTrack)) {
        this.localStream.removeTrack(videoTrack);
      }
    } catch (error) {
      console.warn('Failed to remove video track from local stream:', error);
    }

    this.localVideoTrack = null;
    this.setLocalPreviewStream(null);

    if (stopTrack) {
      this.stopTrackSafely(videoTrack);
    }

    return videoTrack;
  }

  private async rollbackLocalVideoTrack(previousVideoTrack: MediaStreamTrack | null): Promise<void> {
    const peer = this.peerConnection;
    if (!this.isCurrentPeer(peer)) return;
    if (previousVideoTrack && previousVideoTrack.readyState === 'live') {
      if (this.localVideoTrack === previousVideoTrack) return;

      try {
        await this.replaceLocalVideoTrack(previousVideoTrack);
        return;
      } catch (rollbackError) {
        if (!this.isCurrentPeer(peer)) return;
        console.error('Failed to rollback local video track', rollbackError);
      }
    }

    try {
      await this.detachCurrentLocalVideoTrack(true);
    } catch (rollbackError) {
      console.error('Failed to clear local video track after failed negotiation', rollbackError);
    }
  }

  private stopTrackSafely(track: MediaStreamTrack | null | undefined): void {
    if (!track) return;
    if (track.readyState === 'ended') return;

    try {
      track.stop();
    } catch {
      // ignore
    }
  }

  public async toggleVideoCallCameraFacing(): Promise<void> {
    if (!this.peerConnection || this.isVideoToggleInProgress || !this.hasLocalVideoTrack()) return;
    const peer = this.peerConnection;
    if (!this.isCurrentPeer(peer)) return;

    const prevFacing: FacingMode = this.videoCallCameraFacing;
    const previousVideoTrack: MediaStreamTrack | null = this.getLiveLocalVideoTrack();
    let replacedVideoTrack: MediaStreamTrack | null = null;
    this.videoCallCameraFacing = (this.videoCallCameraFacing === 'user') ? 'environment' : 'user';
    this.isVideoToggleInProgress = true;

    try {
      replacedVideoTrack = await this.replaceVideoCallTrack(true);
      if (!this.isCurrentPeer(peer)) return;
      await this.ensureOfferSentOrThrow('toggle_video_call_camera_facing');
      this.stopTrackSafely(replacedVideoTrack);
    } catch (error) {
      if (!this.isCurrentPeer(peer)) return;
      console.error('Failed to switch camera', error);
      this.videoCallCameraFacing = prevFacing;
      await this.rollbackLocalVideoTrack(previousVideoTrack);
      if (!this.isCurrentPeer(peer)) return;
      const actualFacing: FacingMode | null = this.getLocalVideoFacing();
      if (actualFacing) {
        this.videoCallCameraFacing = actualFacing;
      }
      throw error;
    } finally {
      if (this.isCurrentPeer(peer)) this.isVideoToggleInProgress = false;
      else this.stopTrackSafely(previousVideoTrack);
    }
  }

  private async prepareIosBackgroundCameraOutgoingVideoTrack(
    sourceVideoTrack: MediaStreamTrack,
  ): Promise<MediaStreamTrack | null> {
    const peer = this.peerConnection;
    if (!this.isCurrentPeer(peer)) return null;
    if (!this.platformService.isIosApp || !this.orderId || this.isScreenSharing) {
      this.iosBackgroundCameraOutgoingVideoTrack = null;
      return null;
    }

    const outgoingTrack: MediaStreamTrack | null =
      await this.iosVideoCallBackgroundCameraService.prepareOutgoingVideoTrack({
        orderId: this.orderId,
        sourceVideoTrack,
        facingMode: this.videoCallCameraFacing,
        callbacks: {
          releaseForegroundVideoTrack: (): void => {
            if (this.isCurrentPeer(peer)) this.releaseForegroundLocalVideoTrackForIosBackground();
          },
          restoreForegroundVideoTrack: async (isCurrent: () => boolean): Promise<MediaStreamTrack | null> =>
            this.isCurrentPeer(peer) && isCurrent() ? this.restoreForegroundLocalVideoTrackForIosBackground(isCurrent) : null,
          replaceSenderVideoTrack: async (videoTrack: MediaStreamTrack): Promise<void> => {
            if (this.isCurrentPeer(peer)) await this.replaceOrAddLocalVideoSenderTrack(videoTrack);
          },
        },
      });

    if (!this.isCurrentPeer(peer)) return null;
    this.iosBackgroundCameraOutgoingVideoTrack = outgoingTrack;
    return outgoingTrack;
  }

  private releaseForegroundLocalVideoTrackForIosBackground(): void {
    const videoTrack: MediaStreamTrack | null = this.localVideoTrack;
    if (!videoTrack) return;

    try {
      if (this.localStream?.getVideoTracks().includes(videoTrack)) {
        this.localStream.removeTrack(videoTrack);
      }
    } catch (error) {
      console.warn('Failed to remove foreground iOS video track before background capture:', error);
    }

    try {
      videoTrack.stop();
    } catch {
      // ignore
    }

    this.localVideoTrack = null;
    this.setLocalPreviewStream(null);
  }

  private async restoreForegroundLocalVideoTrackForIosBackground(isCurrent: () => boolean): Promise<MediaStreamTrack | null> {
    const peer = this.peerConnection;
    if (!this.isCurrentPeer(peer) || !isCurrent()) return null;
    if (this.isScreenSharing) return null;

    const currentTrack: MediaStreamTrack | null = this.getLiveLocalVideoTrack();
    if (currentTrack) return currentTrack;

    const capture: VideoCallTrackCapture = await this.createVideoCallTrack();
    if (!this.isCurrentPeer(peer) || !isCurrent()) {
      this.stopTrackSafely(capture.videoTrack);
      this.stopTrackSafely(capture.audioTrack);
      return null;
    }
    const videoTrack: MediaStreamTrack = capture.videoTrack;
    this.localVideoTrack = videoTrack;
    this.setLocalPreviewStream(new MediaStream([videoTrack]));

    try {
      if (this.localStream) {
        this.localStream.getVideoTracks().forEach((track: MediaStreamTrack): void => {
          this.localStream?.removeTrack(track);
          if (track !== videoTrack) track.stop();
        });
        this.localStream.addTrack(videoTrack);
      } else {
        this.localStream = new MediaStream([videoTrack]);
      }

      await this.preserveLocalAudioAfterVideoTrackChange('restore_foreground_ios_background', capture.audioTrack);
      if (!this.isCurrentPeer(peer) || !isCurrent()) return null;
    } catch (error) {
      this.stopUnattachedAudioTrack(capture.audioTrack);
      throw error;
    }

    if (typeof this.changeCallback === 'function') {
      this.changeCallback();
    }

    return videoTrack;
  }

  private createVideoSenderStream(videoTrack: MediaStreamTrack): MediaStream {
    return this.localStream ?? new MediaStream([videoTrack]);
  }

  private getLocalVideoSender(): RTCRtpSender | null {
    if (this.localVideoSender) return this.localVideoSender;
    if (!this.peerConnection) return null;

    const transceiver: RTCRtpTransceiver | null = this.getReusableVideoTransceiver();
    return transceiver?.sender ?? null;
  }

  private getLocalAudioSender(): RTCRtpSender | null {
    if (!this.peerConnection) return null;

    const audioSender: RTCRtpSender | undefined =
      this.peerConnection.getSenders().find((sender: RTCRtpSender): boolean => sender.track?.kind === 'audio');
    if (audioSender) return audioSender;

    const audioTransceiver: RTCRtpTransceiver | undefined =
      this.peerConnection.getTransceivers().find((transceiver: RTCRtpTransceiver): boolean => {
        if (transceiver.direction === 'stopped') return false;
        if (transceiver.sender.track) return false;
        return transceiver.receiver.track.kind === 'audio';
      });

    return audioTransceiver?.sender ?? null;
  }

  private getReusableVideoTransceiver(): RTCRtpTransceiver | null {
    if (!this.peerConnection) return null;

    const transceivers: RTCRtpTransceiver[] = this.peerConnection.getTransceivers();
    const activeLocalVideoTransceiver: RTCRtpTransceiver | undefined =
      transceivers.find((transceiver: RTCRtpTransceiver): boolean => {
        return transceiver.sender.track?.kind === 'video';
      });
    if (activeLocalVideoTransceiver) return activeLocalVideoTransceiver;

    const reusableRemoteVideoTransceiver: RTCRtpTransceiver | undefined =
      transceivers.find((transceiver: RTCRtpTransceiver): boolean => {
        if (transceiver.direction === 'stopped') return false;
        if (transceiver.sender.track) return false;
        return transceiver.receiver.track.kind === 'video';
      });
    return reusableRemoteVideoTransceiver ?? null;
  }

  private getVideoTransceiverForSender(sender: RTCRtpSender): RTCRtpTransceiver | null {
    if (!this.peerConnection) return null;

    const transceiver: RTCRtpTransceiver | undefined =
      this.peerConnection.getTransceivers().find((item: RTCRtpTransceiver): boolean => item.sender === sender);
    return transceiver ?? null;
  }

  private enableVideoTransceiverSending(transceiver: RTCRtpTransceiver | null): void {
    if (!transceiver) return;

    if (transceiver.direction === 'recvonly') {
      transceiver.direction = 'sendrecv';
      return;
    }

    if (transceiver.direction === 'inactive') {
      transceiver.direction = 'sendonly';
    }
  }

  private disableVideoTransceiverSending(sender: RTCRtpSender | null): void {
    if (!sender) return;

    const transceiver: RTCRtpTransceiver | null = this.getVideoTransceiverForSender(sender);
    if (!transceiver) return;

    if (transceiver.direction === 'sendrecv') {
      transceiver.direction = 'recvonly';
      return;
    }

    if (transceiver.direction === 'sendonly') {
      transceiver.direction = 'inactive';
    }
  }

  private updateLocalVideoSenderStreams(sender: RTCRtpSender, videoTrack: MediaStreamTrack): void {
    try {
      sender.setStreams(this.createVideoSenderStream(videoTrack));
    } catch (error) {
      console.warn('Unable to update sender streams:', error);
    }
  }

  private async replaceOrAddLocalVideoSenderTrack(videoTrack: MediaStreamTrack): Promise<void> {
    const peer = this.peerConnection;
    if (!this.isCurrentPeer(peer)) throw new Error('Peer connection is not initialized!');

    const sender: RTCRtpSender | null = this.getLocalVideoSender();
    if (sender) {
      this.enableVideoTransceiverSending(this.getVideoTransceiverForSender(sender));
      this.updateLocalVideoSenderStreams(sender, videoTrack);
      await sender.replaceTrack(videoTrack);
      if (!this.isCurrentPeer(peer)) return;
      this.localVideoSender = sender;
      return;
    }

    const streamForSender: MediaStream = this.createVideoSenderStream(videoTrack);
    this.localVideoSender = this.peerConnection.addTrack(videoTrack, streamForSender);
  }

  private async deactivateIosBackgroundCameraOutgoingVideoTrack(): Promise<void> {
    if (!this.platformService.isIosApp) return;

    this.iosBackgroundCameraOutgoingVideoTrack = null;
    await this.iosVideoCallBackgroundCameraService.deactivate();
  }

  private async replaceLocalVideoTrack(
    videoTrack: MediaStreamTrack,
    preservePreviousTrack: boolean = false,
  ): Promise<MediaStreamTrack | null> {
    const peer = this.peerConnection;
    if (!this.isCurrentPeer(peer)) throw new Error('Peer connection is not initialized!');

    const previousVideoTrack: MediaStreamTrack | null = this.localVideoTrack;
    const existingLocalStreamVideoTracks: MediaStreamTrack[] = this.localStream?.getVideoTracks() ?? [];
    const preparedSenderVideoTrack: MediaStreamTrack | null =
      await this.prepareIosBackgroundCameraOutgoingVideoTrack(videoTrack);
    if (!this.isCurrentPeer(peer)) {
      this.stopTrackSafely(videoTrack);
      return null;
    }
    const senderVideoTrack: MediaStreamTrack = preparedSenderVideoTrack ?? videoTrack;

    await this.replaceOrAddLocalVideoSenderTrack(senderVideoTrack);
    if (!this.isCurrentPeer(peer)) {
      this.stopTrackSafely(videoTrack);
      return null;
    }

    this.localVideoTrack = videoTrack;
    this.setLocalPreviewStream(new MediaStream([videoTrack]));

    if (this.localStream) {
      existingLocalStreamVideoTracks.forEach((track: MediaStreamTrack): void => {
        this.localStream.removeTrack(track);
      });
      this.localStream.addTrack(videoTrack);
    } else {
      this.localStream = new MediaStream([videoTrack]);
    }

    if (!preservePreviousTrack) {
      existingLocalStreamVideoTracks.forEach((track: MediaStreamTrack): void => {
        if (track !== videoTrack) {
          this.stopTrackSafely(track);
        }
      });
    }

    if (previousVideoTrack
      && previousVideoTrack !== videoTrack
      && !preservePreviousTrack
      && !existingLocalStreamVideoTracks.includes(previousVideoTrack)) {
      this.stopTrackSafely(previousVideoTrack);
    }

    return previousVideoTrack && previousVideoTrack !== videoTrack ? previousVideoTrack : null;
  }

  private async replaceVideoCallTrack(preservePreviousTrack: boolean = false): Promise<MediaStreamTrack | null> {
    const peer = this.peerConnection;
    if (!this.isCurrentPeer(peer)) return null;
    try {
      return await this.createAndReplaceVideoCallTrack(preservePreviousTrack);
    } catch (error) {
      if (!this.isCurrentPeer(peer)) return null;
      if (!this.shouldRetryVideoTrack(error)) {
        throw error;
      }
      await this.stopLocalVideoTrackForRetry();
      if (!this.isCurrentPeer(peer)) return null;
      return await this.createAndReplaceVideoCallTrack(preservePreviousTrack);
    }
  }

  private async createAndReplaceVideoCallTrack(preservePreviousTrack: boolean): Promise<MediaStreamTrack | null> {
    const peer = this.peerConnection;
    if (!this.isCurrentPeer(peer)) return null;
    const capture: VideoCallTrackCapture = await this.createVideoCallTrack();
    if (!this.isCurrentPeer(peer)) {
      this.stopTrackSafely(capture.videoTrack);
      this.stopTrackSafely(capture.audioTrack);
      return null;
    }

    try {
      const replacedVideoTrack: MediaStreamTrack | null =
        await this.replaceLocalVideoTrack(capture.videoTrack, preservePreviousTrack);
      if (!this.isCurrentPeer(peer)) {
        this.stopTrackSafely(capture.audioTrack);
        this.stopTrackSafely(replacedVideoTrack);
        return null;
      }
      await this.preserveLocalAudioAfterVideoTrackChange('replace_video_call_track', capture.audioTrack);
      return replacedVideoTrack;
    } catch (error) {
      this.stopUnattachedVideoTrack(capture.videoTrack);
      this.stopUnattachedAudioTrack(capture.audioTrack);
      throw error;
    }
  }

  private stopUnattachedVideoTrack(videoTrack: MediaStreamTrack): void {
    if (this.localVideoTrack === videoTrack) return;
    if (this.localStream?.getVideoTracks().includes(videoTrack)) return;
    if (this.localVideoSender?.track === videoTrack) return;
    this.stopTrackSafely(videoTrack);
  }

  private stopUnattachedAudioTrack(audioTrack: MediaStreamTrack | null): void {
    if (!audioTrack) return;
    if (this.localStream?.getAudioTracks().some((track: MediaStreamTrack): boolean => track.id === audioTrack.id)) return;
    if (this.getLocalAudioSender()?.track?.id === audioTrack.id) return;
    this.stopTrackSafely(audioTrack);
  }

  private shouldCaptureAudioWithVideo(): boolean {
    if (!this.platformService.isIos) return false;
    return this.shouldPreserveLocalAudioForVideoChange();
  }

  private shouldPreserveLocalAudioForVideoChange(): boolean {
    if (this.localStream?.getAudioTracks().length) return true;
    return !!this.getLocalAudioSender();
  }

  private async preserveLocalAudioAfterVideoTrackChange(
    context: string,
    audioTrack: MediaStreamTrack | null,
  ): Promise<void> {
    if (!this.shouldPreserveLocalAudioForVideoChange()) {
      this.stopTrackSafely(audioTrack);
      return;
    }

    if (audioTrack) {
      await this.replaceLocalAudioTrack(audioTrack, context);
      return;
    }

    await this.ensureUsableLocalAudioTrackAfterVideoChange(context);
  }

  private async ensureUsableLocalAudioTrackAfterVideoChange(context: string): Promise<void> {
    const peer = this.peerConnection;
    if (!this.isCurrentPeer(peer)) return;
    const audioSender: RTCRtpSender | null = this.getLocalAudioSender();
    const senderTrack: MediaStreamTrack | null = audioSender?.track ?? null;
    const localAudioTrack: MediaStreamTrack | null = this.localStream?.getAudioTracks()[0] ?? null;
    const shouldEnableTrack: boolean = localAudioTrack?.enabled ?? senderTrack?.enabled ?? true;

    if (this.isUsableAudioTrack(senderTrack)) {
      senderTrack.enabled = shouldEnableTrack;
      this.ensureLocalStreamContainsAudioTrack(senderTrack);
      return;
    }

    if (this.isUsableAudioTrack(localAudioTrack)) {
      localAudioTrack.enabled = shouldEnableTrack;
      await this.replaceOrAddLocalAudioSenderTrack(localAudioTrack);
      if (!this.isCurrentPeer(peer)) return;
      this.ensureLocalStreamContainsAudioTrack(localAudioTrack);
      return;
    }

    const preferredDeviceId: string | null = this.getPreferredAudioDeviceId(localAudioTrack, senderTrack);
    const replacementTrack: MediaStreamTrack =
      await this.createReplacementAudioTrack(preferredDeviceId, context, true);
    if (!this.isCurrentPeer(peer)) {
      this.stopTrackSafely(replacementTrack);
      return;
    }
    await this.replaceLocalAudioTrack(replacementTrack, context);
  }

  private async replaceLocalAudioTrack(audioTrack: MediaStreamTrack, context: string): Promise<void> {
    const peer = this.peerConnection;
    if (!this.isCurrentPeer(peer)) {
      this.stopTrackSafely(audioTrack);
      return;
    }
    const previousAudioTracks: MediaStreamTrack[] = this.localStream?.getAudioTracks() ?? [];
    const audioSender: RTCRtpSender | null = this.getLocalAudioSender();
    const previousSenderTrack: MediaStreamTrack | null = audioSender?.track ?? null;
    const shouldEnableTrack: boolean = previousAudioTracks[0]?.enabled ?? previousSenderTrack?.enabled ?? true;
    let senderWasUpdated: boolean = false;

    audioTrack.enabled = shouldEnableTrack;

    try {
      await this.replaceOrAddLocalAudioSenderTrack(audioTrack);
      if (!this.isCurrentPeer(peer)) {
        this.stopTrackSafely(audioTrack);
        return;
      }
      senderWasUpdated = true;
      this.setLocalAudioTrack(audioTrack);
      this.stopReplacedAudioTracks(previousAudioTracks, previousSenderTrack, audioTrack);
    } catch (error) {
      console.warn('[WebrtcAudioService] Failed to replace local audio track', {
        context,
        error,
      });
      if (!senderWasUpdated) {
        this.stopTrackSafely(audioTrack);
      }
      throw error;
    }
  }

  private async replaceOrAddLocalAudioSenderTrack(audioTrack: MediaStreamTrack): Promise<void> {
    if (!this.peerConnection) throw new Error('Peer connection is not initialized!');

    const audioSender: RTCRtpSender | null = this.getLocalAudioSender();
    if (audioSender) {
      await audioSender.replaceTrack(audioTrack);
      return;
    }

    const senderStream: MediaStream = new MediaStream([audioTrack]);
    const sender: RTCRtpSender = this.peerConnection.addTrack(audioTrack, senderStream);
    if (environment.highMediaQuality) {
      await super.setAudioQuality(sender, this.AUDIO_BITRATE);
    }
  }

  private setLocalAudioTrack(audioTrack: MediaStreamTrack): void {
    if (!this.localStream) {
      this.localStream = new MediaStream([audioTrack]);
      return;
    }

    this.localStream.getAudioTracks().forEach((track: MediaStreamTrack): void => {
      this.localStream?.removeTrack(track);
    });
    this.localStream.addTrack(audioTrack);
  }

  private ensureLocalStreamContainsAudioTrack(audioTrack: MediaStreamTrack): void {
    if (!this.localStream) {
      this.localStream = new MediaStream([audioTrack]);
      return;
    }

    if (this.localStream.getAudioTracks().some((track: MediaStreamTrack): boolean => track.id === audioTrack.id)) return;

    const staleAudioTracks: MediaStreamTrack[] = this.localStream.getAudioTracks();
    staleAudioTracks.forEach((track: MediaStreamTrack): void => {
      this.localStream?.removeTrack(track);
      this.stopTrackSafely(track);
    });
    this.localStream.addTrack(audioTrack);
  }

  private stopReplacedAudioTracks(
    previousAudioTracks: MediaStreamTrack[],
    previousSenderTrack: MediaStreamTrack | null,
    nextAudioTrack: MediaStreamTrack,
  ): void {
    previousAudioTracks.forEach((track: MediaStreamTrack): void => {
      if (track.id !== nextAudioTrack.id) {
        this.stopTrackSafely(track);
      }
    });

    if (!previousSenderTrack || previousSenderTrack.id === nextAudioTrack.id) return;

    const senderTrackWasStoppedWithLocalTracks: boolean =
      previousAudioTracks.some((track: MediaStreamTrack): boolean => track.id === previousSenderTrack.id);
    if (senderTrackWasStoppedWithLocalTracks) return;

    this.stopTrackSafely(previousSenderTrack);
  }

  private isUsableAudioTrack(track: MediaStreamTrack | null): track is MediaStreamTrack {
    return track?.kind === 'audio'
      && track.readyState === 'live'
      && track.muted !== true;
  }

  private async createReplacementAudioTrack(
    preferredDeviceId: string | null,
    context: string,
    allowFallback: boolean,
  ): Promise<MediaStreamTrack> {
    const peer = this.peerConnection;
    if (!this.isCurrentPeer(peer)) throw new Error('Peer connection is not initialized!');
    try {
      return await this.createAudioTrackFromConstraints(this.buildAudioOnlyConstraints(preferredDeviceId));
    } catch (error) {
      if (!this.isCurrentPeer(peer) || !allowFallback || !preferredDeviceId) {
        throw error;
      }

      console.warn('[WebrtcAudioService] Preferred microphone unavailable during audio recovery', {
        context,
        error,
      });
      return await this.createAudioTrackFromConstraints(this.buildAudioOnlyConstraints(null));
    }
  }

  private async createAudioTrackFromConstraints(constraints: MediaStreamConstraints): Promise<MediaStreamTrack> {
    if (typeof navigator === 'undefined') {
      throw new Error('Navigator is not available.');
    }

    const stream: MediaStream = await navigator.mediaDevices.getUserMedia(constraints);
    const audioTrack: MediaStreamTrack | null = stream.getAudioTracks()[0] ?? null;

    if (!audioTrack) {
      stream.getTracks().forEach((track: MediaStreamTrack): void => this.stopTrackSafely(track));
      throw new Error('Audio track is not available.');
    }

    stream.getTracks().forEach((track: MediaStreamTrack): void => {
      if (track !== audioTrack) {
        this.stopTrackSafely(track);
      }
    });

    return audioTrack;
  }

  private buildAudioOnlyConstraints(preferredDeviceId: string | null): MediaStreamConstraints {
    const audioConstraints: boolean | MediaTrackConstraints = this.normalizeAudioConstraints();

    if (!preferredDeviceId) {
      return {
        audio: audioConstraints,
        video: false,
      };
    }

    const constrainedAudio: MediaTrackConstraints = typeof audioConstraints === 'object'
      ? {
        ...audioConstraints,
        deviceId: { exact: preferredDeviceId },
      }
      : {
        deviceId: { exact: preferredDeviceId },
      };

    return {
      audio: constrainedAudio,
      video: false,
    };
  }

  private getPreferredAudioDeviceId(
    localAudioTrack: MediaStreamTrack | null,
    senderTrack: MediaStreamTrack | null,
  ): string | null {
    return this.getAudioTrackDeviceId(localAudioTrack) ?? this.getAudioTrackDeviceId(senderTrack);
  }

  private getAudioTrackDeviceId(track: MediaStreamTrack | null): string | null {
    if (!track) return null;

    const settings: MediaTrackSettings = track.getSettings();
    const deviceId: unknown = settings.deviceId;
    if (typeof deviceId === 'string' && deviceId.length > 0) {
      return deviceId;
    }

    return null;
  }

  private normalizeVideoConstraints(): MediaTrackConstraints {
    const baseConstraints = getVideoConstraints();

    if (typeof baseConstraints.video === 'object' && baseConstraints.video) {
      return { ...baseConstraints.video };
    }

    return {};
  }

  private normalizeAudioConstraints(): boolean | MediaTrackConstraints {
    const baseConstraints: MediaStreamConstraints = getAudioConstraints();
    const audioConstraints: boolean | MediaTrackConstraints | undefined = baseConstraints.audio;

    if (audioConstraints && typeof audioConstraints === 'object') {
      return { ...audioConstraints };
    }

    if (typeof audioConstraints === 'boolean') {
      return audioConstraints;
    }

    return true;
  }

  private async ensureVideoTrackHasRenderableFrame(videoTrack: MediaStreamTrack): Promise<void> {
    if (!this.shouldValidateLocalVideoFrame()) return;
    if (videoTrack.readyState !== 'live') {
      throw this.createVideoTrackFrameError('Video track ended before first frame.');
    }

    await this.waitForVideoTrackFrame(videoTrack);
  }

  private shouldValidateLocalVideoFrame(): boolean {
    if (this.platformService.isSSR) return false;
    if (!this.appStateChangeService.appIsActive) return false;
    if (typeof document === 'undefined') return false;
    if (typeof MediaStream === 'undefined') return false;
    return !!document.body;
  }

  private async waitForVideoTrackFrame(videoTrack: MediaStreamTrack): Promise<void> {
    const videoElement: HTMLVideoElement = document.createElement('video');
    const validationStream: MediaStream = new MediaStream([videoTrack]);

    videoElement.autoplay = true;
    videoElement.muted = true;
    videoElement.playsInline = true;
    videoElement.setAttribute('playsinline', '');
    videoElement.setAttribute('webkit-playsinline', '');
    videoElement.style.position = 'fixed';
    videoElement.style.right = '0';
    videoElement.style.bottom = '0';
    videoElement.style.width = '2px';
    videoElement.style.height = '2px';
    videoElement.style.opacity = '0.01';
    videoElement.style.pointerEvents = 'none';
    videoElement.style.overflow = 'hidden';
    videoElement.style.zIndex = '-1';
    videoElement.srcObject = validationStream;

    document.body.appendChild(videoElement);

    try {
      try {
        await videoElement.play();
      } catch {
        throw this.createVideoTrackFrameError('Unable to play local video validation element.');
      }

      await this.waitForVideoElementFrame(videoElement, videoTrack);
    } finally {
      videoElement.pause();
      videoElement.srcObject = null;
      videoElement.remove();
    }
  }

  private waitForVideoElementFrame(
    videoElement: HTMLVideoElement,
    videoTrack: MediaStreamTrack,
  ): Promise<void> {
    return new Promise<void>((resolve, reject): void => {
      let settled: boolean = false;
      let frameRequestId: number | null = null;
      let timeoutId: ReturnType<typeof globalThis.setTimeout> | null = null;
      let cleanup: () => void = (): void => {};

      const finish = (error: Error | null): void => {
        if (settled) return;
        settled = true;
        cleanup();

        if (error) {
          reject(error);
          return;
        }

        resolve();
      };

      const handleFrameReady = (): void => {
        if (!this.hasRenderableVideoFrame(videoElement)) return;
        finish(null);
      };

      const handleError = (): void => {
        finish(this.createVideoTrackFrameError('Video validation element failed before first frame.'));
      };

      const handleTrackEnded = (): void => {
        finish(this.createVideoTrackFrameError('Video track ended before first frame.'));
      };

      cleanup = (): void => {
        if (timeoutId) {
          globalThis.clearTimeout(timeoutId);
          timeoutId = null;
        }

        videoElement.removeEventListener('loadedmetadata', handleFrameReady);
        videoElement.removeEventListener('loadeddata', handleFrameReady);
        videoElement.removeEventListener('canplay', handleFrameReady);
        videoElement.removeEventListener('playing', handleFrameReady);
        videoElement.removeEventListener('resize', handleFrameReady);
        videoElement.removeEventListener('error', handleError);
        videoTrack.removeEventListener('ended', handleTrackEnded);

        if (frameRequestId !== null && typeof videoElement.cancelVideoFrameCallback === 'function') {
          videoElement.cancelVideoFrameCallback(frameRequestId);
          frameRequestId = null;
        }
      };

      timeoutId = globalThis.setTimeout((): void => {
        finish(this.createVideoTrackFrameError('Video track did not produce first frame.'));
      }, this.localVideoFrameReadyTimeoutMs);

      videoElement.addEventListener('loadedmetadata', handleFrameReady);
      videoElement.addEventListener('loadeddata', handleFrameReady);
      videoElement.addEventListener('canplay', handleFrameReady);
      videoElement.addEventListener('playing', handleFrameReady);
      videoElement.addEventListener('resize', handleFrameReady);
      videoElement.addEventListener('error', handleError);
      videoTrack.addEventListener('ended', handleTrackEnded);

      if (typeof videoElement.requestVideoFrameCallback === 'function') {
        frameRequestId = videoElement.requestVideoFrameCallback((): void => handleFrameReady());
      }

      handleFrameReady();
    });
  }

  private hasRenderableVideoFrame(videoElement: HTMLVideoElement): boolean {
    return videoElement.videoWidth > 0
      && videoElement.videoHeight > 0
      && videoElement.readyState >= this.videoReadyStateHaveCurrentData;
  }

  private createVideoTrackFrameError(message: string): Error {
    const error: Error = new Error(message);
    error.name = 'AbortError';
    return error;
  }

  private async createVideoCallTrack(): Promise<VideoCallTrackCapture> {
    const peer = this.peerConnection;
    if (!this.isCurrentPeer(peer)) throw new Error('Peer connection is not initialized!');
    if (typeof navigator === 'undefined') {
      throw new Error('Navigator is not available.');
    }
    const videoConstraints: MediaTrackConstraints = this.normalizeVideoConstraints();
    videoConstraints.facingMode = { ideal: this.videoCallCameraFacing };
    const shouldCaptureAudio: boolean = this.shouldCaptureAudioWithVideo();
    const audioConstraints: boolean | MediaTrackConstraints = shouldCaptureAudio
      ? this.normalizeAudioConstraints()
      : false;
    let stream: MediaStream;

    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: videoConstraints,
        audio: audioConstraints,
      });
    } catch (error) {
      if (!this.isCurrentPeer(peer)) throw error;
      if (this.videoCallCameraFacing === 'environment') {
        console.warn('Failed to access rear camera, falling back to front camera.', error);
        this.videoCallCameraFacing = 'user';
        return this.createVideoCallTrack();
      }
      throw error;
    }
    if (!this.isCurrentPeer(peer)) {
      stream.getTracks().forEach((track: MediaStreamTrack): void => this.stopTrackSafely(track));
      throw new Error('Call ended during camera capture.');
    }
    const videoTrack: MediaStreamTrack | undefined = stream.getVideoTracks()[0];

    if (!videoTrack) {
      stream.getTracks().forEach((track: MediaStreamTrack): void => track.stop());
      throw new Error('Video track is not available.');
    }

    const audioTrack: MediaStreamTrack | null = shouldCaptureAudio
      ? stream.getAudioTracks()[0] ?? null
      : null;

    if (shouldCaptureAudio && !audioTrack) {
      stream.getTracks().forEach((track: MediaStreamTrack): void => this.stopTrackSafely(track));
      throw new Error('Audio track is not available for video call.');
    }

    stream.getAudioTracks().forEach((track: MediaStreamTrack): void => {
      if (track !== audioTrack) {
        this.stopTrackSafely(track);
      }
    });

    try {
      await this.ensureVideoTrackHasRenderableFrame(videoTrack);
      if (!this.isCurrentPeer(peer)) throw new Error('Call ended during camera preparation.');
    } catch (error) {
      stream.getTracks().forEach((track: MediaStreamTrack): void => this.stopTrackSafely(track));
      throw error;
    }

    this.updateVideoCallFacingFromTrack(videoTrack);

    return {
      videoTrack,
      audioTrack,
    };
  }

  public async startScreenShare(): Promise<void> {
    if (this.isScreenSharing) return;
    const peer = this.peerConnection;
    if (!this.isCurrentPeer(peer)) throw new Error('Peer connection is not initialized!');
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getDisplayMedia) {
      throw new Error('Screen sharing is not supported on this device.');
    }

    let displayStream: MediaStream;
    try {
      displayStream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
    } catch (error) {
      if (!this.isCurrentPeer(peer)) return;
      throw error;
    }
    if (!this.isCurrentPeer(peer)) {
      displayStream.getTracks().forEach((track: MediaStreamTrack): void => this.stopTrackSafely(track));
      return;
    }
    const displayTrack: MediaStreamTrack | undefined = displayStream.getVideoTracks()[0];

    if (!displayTrack) {
      displayStream.getTracks().forEach((track: MediaStreamTrack): void => track.stop());
      throw new Error('Screen video track is not available.');
    }

    displayTrack.onended = () => {
      this.ngZone.run(() => {
        if (this.isCurrentPeer(peer) && this.isScreenSharing && this.screenShareTrack === displayTrack) {
          this.stopScreenShare().catch(() => undefined);
        }
      });
    };

    try {
      await this.deactivateIosBackgroundCameraOutgoingVideoTrack();
      if (!this.isCurrentPeer(peer)) return;

      this.wasCameraEnabledBeforeScreenShare = this.hasLocalVideoTrack();
      this.preservedCameraTrack = this.wasCameraEnabledBeforeScreenShare ? this.localVideoTrack : null;
      this.preservedCameraStream = this.preservedCameraTrack
        ? this.localPreviewStream ?? new MediaStream([this.preservedCameraTrack]) : null;
      this.isScreenSharing = true;
      await this.replaceLocalVideoTrack(displayTrack, true);
      if (!this.isCurrentPeer(peer)) return;
      this.screenShareTrack = displayTrack;
      this.screenShareStream = displayStream;
      await this.ensureOfferSentOrThrow('start_screen_share');
    } catch (error) {
      if (!this.isCurrentPeer(peer)) return;
      if (this.screenShareTrack !== displayTrack) this.isScreenSharing = false;
      throw error;
    } finally {
      if (!this.isCurrentPeer(peer) || this.screenShareTrack !== displayTrack) {
        displayTrack.onended = null;
        displayStream.getTracks().forEach((track: MediaStreamTrack): void => this.stopTrackSafely(track));
      }
    }
  }

  public async stopScreenShare(): Promise<void> {
    if (!this.isScreenSharing) return;
    const peer = this.peerConnection;
    if (!this.isCurrentPeer(peer)) return;

    const needRestoreCamera: boolean = this.wasCameraEnabledBeforeScreenShare;
    const preservedCameraTrack = this.preservedCameraTrack;
    const preservedCameraStream = this.preservedCameraStream;
    this.wasCameraEnabledBeforeScreenShare = false;
    let stopScreenShareError: unknown = null;

    try {
      if (this.screenShareStream) {
        this.screenShareStream.getTracks().forEach((track: MediaStreamTrack): void => track.stop());
      }
      this.screenShareStream = null;
      this.screenShareTrack = null;
      this.isScreenSharing = false;

      if (needRestoreCamera && preservedCameraTrack && preservedCameraTrack.readyState === 'live') {
        await this.replaceLocalVideoTrack(preservedCameraTrack, true);
        if (!this.isCurrentPeer(peer)) return;
        this.setLocalPreviewStream(preservedCameraStream ?? new MediaStream([preservedCameraTrack]));
      } else if (needRestoreCamera) {
        const capture: VideoCallTrackCapture = await this.createVideoCallTrack();
        try {
          if (!this.isCurrentPeer(peer)) {
            this.stopTrackSafely(capture.videoTrack);
            this.stopTrackSafely(capture.audioTrack);
            return;
          }
          await this.replaceLocalVideoTrack(capture.videoTrack);
          if (!this.isCurrentPeer(peer)) {
            this.stopTrackSafely(capture.audioTrack);
            return;
          }
          await this.preserveLocalAudioAfterVideoTrackChange('stop_screen_share_restore_camera', capture.audioTrack);
        } catch (error) {
          this.stopUnattachedVideoTrack(capture.videoTrack);
          this.stopUnattachedAudioTrack(capture.audioTrack);
          throw error;
        }
      } else {
        await this.disableLocalVideo(true, true);
      }
    } catch (error) {
      if (!this.isCurrentPeer(peer)) return;
      stopScreenShareError = error;
      throw error;
    } finally {
      if (this.isCurrentPeer(peer)) {
        this.preservedCameraTrack = null;
        this.preservedCameraStream = null;
        this.isScreenSharing = false;
        const isOfferSent: boolean = await this.sendOffer();
        if (this.isCurrentPeer(peer) && !isOfferSent && !stopScreenShareError) {
          throw new Error('[WebrtcAudioService] Offer was not delivered (stop_screen_share)');
        }
      } else {
        this.stopTrackSafely(preservedCameraTrack);
      }
    }
  }

  public disableCameraRestoreAfterScreenShare(): void {
    this.wasCameraEnabledBeforeScreenShare = false;

    if (this.preservedCameraTrack) {
      try {
        this.preservedCameraTrack.stop();
      } catch {
        // ignore
      }
    }
    this.preservedCameraTrack = null;
    this.preservedCameraStream = null;
  }

  public async tryToPlay(): Promise<void> {
    if (!this.mediaElement) return;
    this.resumeAudioContextIfNeeded();
    if (this.playPending) {
      console.warn('Play is already pending, skipping this call');
      return;
    }
    if (!this.mediaElement.paused) return;

    if (!this.appStateChangeService.userIsActive) {
      this.audioCallConnectionDebugService.milestone('playback.user_gesture_needed', undefined, 'warning');
      console.warn('User interaction required, showing play button');
      this.showButtonPlaySubject.next(true);
      return;
    }

    try {
      this.playPending = this.mediaElement.play();
      await this.playPending;
    } catch (error) {
      this.audioCallConnectionDebugService.milestone('playback.failed', error, 'warning');
      if (error instanceof DOMException && (error.name === 'AbortError' || error.name === 'NotAllowedError')) {
        this.showButtonPlaySubject.next(true);
      } else {
        console.error('Error trying to play audio:', error);
      }
    } finally {
      this.playPending = null;
    }
  }

  private addMediaElementListeners(): void {
    if (!this.mediaElement) return;

    this.mediaElementSb.sub = fromEvent(this.mediaElement, 'playing').subscribe(this.onMediaPlayingHandler);
    this.mediaElementSb.sub = fromEvent(this.mediaElement, 'pause').subscribe(this.onMediaPauseHandler);
    this.mediaElementSb.sub = fromEvent(this.mediaElement, 'error').subscribe(this.onMediaErrorHandler);

    this.mediaElementSb.sub = fromEvent(this.mediaElement, 'canplay').subscribe(async () => {
      if (this.mediaElement?.paused && !this.playPending) {
        await this.tryToPlay();
      }
    });

    this.mediaElementSb.sub = this.appStateChangeService.appStateChange$.pipe(
      filter((isActive: boolean): boolean => isActive)
    ).subscribe(async (): Promise<void> => {
      this.resumeAudioContextIfNeeded();

      if (this.mediaElement?.paused) {
        await this.tryToPlay();
      }
    });
  }

  private onMediaPlayingHandler = (): void => {
    this.audioCallConnectionDebugService.milestone('playback.playing');
    this.showButtonPlaySubject.next(false);
  };

  private onMediaPauseHandler = async (): Promise<void> => {
    this.audioCallConnectionDebugService.milestone('playback.paused');
    this.showButtonPlaySubject.next(true);
  };

  private onMediaErrorHandler = (event: Event): void => {
    const target: EventTarget | null = event.target;
    if (target instanceof HTMLMediaElement) {
      console.error('Media Error:', target.error);
    }
  };

  private clearMediaElementListeners(): void {
    this.mediaElementSb.unsubscribeAll();
  }

  private stopMedia(): void {
    void this.deactivateIosBackgroundCameraOutgoingVideoTrack();
    this.detachRemoteSourceStream();
    this.clearRemoteTrackListeners();

    if (this.screenShareStream) {
      this.screenShareStream.getTracks().forEach((track: MediaStreamTrack): void => track.stop());
      this.screenShareStream = null;
    }
    if (this.screenShareTrack) {
      try {
        this.screenShareTrack.stop();
      } catch {
        // ignore
      }
      this.screenShareTrack = null;
    }
    if (this.preservedCameraTrack) {
      try {
        this.preservedCameraTrack.stop();
      } catch {
        // ignore
      }
      this.preservedCameraTrack = null;
    }
    this.preservedCameraStream = null;
    this.wasCameraEnabledBeforeScreenShare = false;
    this.isScreenSharing = false;

    if (this.localStream) {
      this.localStream.getTracks().forEach((track: MediaStreamTrack): void => {
        track.stop();
      });
      this.localStream = null;

    }

    if (this.remoteStream) {
      this.remoteStream.getTracks().forEach((track: MediaStreamTrack): void => {
        track.stop();
      });
      this.remoteStream = null;
    }

    if (this.remotePlaybackStream) {
      this.remotePlaybackStream.getTracks().forEach((track: MediaStreamTrack): void => track.stop());
      this.remotePlaybackStream = null;
    }

    this.remoteStreamSubject.next(null);
    this.resetRemoteVideoActivity();
    this.localVideoTrack = null;
    this.setLocalPreviewStream(null);

    if (this.mediaElement) {
      this.clearMediaElementListeners();
      this.mediaElement.srcObject = null;
      this.mediaElement = null;
    }
  }

  private removeRemoteTrack(track: MediaStreamTrack): void {
    this.detachRemoteTrackListeners(track);

    if (this.remoteStream) {
      const targetTrack: MediaStreamTrack | undefined = this.remoteStream.getTracks().find((item: MediaStreamTrack): boolean => item.id === track.id);
      if (targetTrack) {
        this.remoteStream.removeTrack(targetTrack);
        this.detachRemoteTrackListeners(targetTrack);
      }

      if (!this.remoteStream.getTracks().length) {
        this.remoteStream = null;
        this.detachRemoteSourceStream();
      }
    }

    if (this.remotePlaybackStream) {
      const playbackTrack: MediaStreamTrack | undefined = this.remotePlaybackStream.getTracks().find((item: MediaStreamTrack): boolean => item.id === track.id);
      if (playbackTrack) {
        this.remotePlaybackStream.removeTrack(playbackTrack);
      }
    }

    this.remoteStreamSubject.next(this.remoteStream);
    this.syncRemoteVideoActivity();
  }

  private async addLocalTracksToPeerConnection(peer: RTCPeerConnection): Promise<void> {
    if (!this.localStream) throw new Error('Local media stream is not initialized');

    for (const track of this.localStream.getTracks()) {
      if (!this.isCurrentPeer(peer)) return;
      const sender: RTCRtpSender = peer.addTrack(track, this.localStream);
      if (environment.highMediaQuality && track.kind === 'audio') {
        try {
          await super.setAudioQuality(sender, this.AUDIO_BITRATE);
        } catch {
          // Bitrate tuning is optional; adding the microphone sender is not.
          console.warn('Could not apply preferred audio bitrate');
        }
      }
    }
  }

  private async sendOffer(iceRestart: boolean = false): Promise<boolean> {
    const pending: {delivery: Promise<boolean>} | null = await this.prepareAndSendOffer(iceRestart);
    return pending ? await pending.delivery : false;
  }

  private async prepareAndSendOffer(iceRestart: boolean): Promise<{delivery: Promise<boolean>} | null> {
    if (!this.peerConnection) {
      console.error('Peer connection is not initialized!');
      return null;
    }
    if (!this.partnerId) {
      console.error('Not found partnerId!', this.partnerId);
      return null;
    }
    if (!this.orderId) {
      console.error('Not found orderId!', this.orderId);
      return null;
    }
    if (this.peerConnection.signalingState !== 'stable') {
      console.warn('[WebrtcAudioService] Offer was not created: signaling state is not stable', {
        signalingState: this.peerConnection.signalingState,
        partnerId: this.partnerId,
        orderId: this.orderId,
      });
      return null;
    }

    let localOfferWasSet: boolean = false;
    const peer: RTCPeerConnection = this.peerConnection;
    const partnerId: number = this.partnerId;
    const orderId: number = this.orderId;
    const operation: object = {};

    try {
      this.allCandidates = { local: [], remote: [] };

      const offer: RTCSessionDescriptionInit = await super.createOffer(peer, true, false, true, true, iceRestart);
      if (!this.isCurrentPeer(peer)) return null;
      await peer.setLocalDescription(offer);
      if (!this.isCurrentPeer(peer)) return null;
      this.localOfferOperation = operation;
      localOfferWasSet = true;
      return {delivery: this.deliverPreparedOffer(peer, partnerId, orderId, offer, operation)};

    } catch (error) {
      if (localOfferWasSet) {
        await this.rollbackLocalOfferIfNeeded(peer, 'send_offer_error', operation);
      }
      if (!this.isCurrentPeer(peer)) return null;
      console.error('Error during initiator process:', error);
      return null;
    }
  }

  private async deliverPreparedOffer(peer: RTCPeerConnection, partnerId: number, orderId: number, offer: RTCSessionDescriptionInit, operation: object): Promise<boolean> {
    try {
      const result: AudioCallReliableSendResult = await this.webSocketController.emitAudioCallOffer(
        partnerId, orderId, offer, (): boolean => this.isCurrentPeer(peer),
        this.callSessionService.getAttempt(orderId, this.callSessionService.selectedOrder?.myUserId),
      );
      if (!this.isCurrentPeer(peer)) return false;
      if (result.delivered) return true;
      console.warn('[WebrtcAudioService] Offer was not delivered', {status: result.status, orderId});
    } catch {
      if (!this.isCurrentPeer(peer)) return false;
      console.warn('[WebrtcAudioService] Offer delivery failed', {orderId});
    }
    await this.rollbackLocalOfferIfNeeded(peer, 'offer_not_delivered', operation);
    return false;
  }

  private isCurrentPeer(peer: RTCPeerConnection | null): peer is RTCPeerConnection {
    return !!peer && this.peerConnection === peer && peer.connectionState !== 'closed' && peer.signalingState !== 'closed';
  }

  private async rollbackLocalOfferIfNeeded(peer: RTCPeerConnection, context: string, operation: object): Promise<void> {
    if (!this.isCurrentPeer(peer) || this.localOfferOperation !== operation
      || peer.signalingState !== 'have-local-offer') return;

    const rollbackDescription: RTCSessionDescriptionInit = { type: 'rollback' };

    try {
      await peer.setLocalDescription(rollbackDescription);
    } catch (rollbackError) {
      console.warn('[WebrtcAudioService] Local offer rollback failed', {
        context,
        rollbackError,
      });
    }
  }

  private async ensureOfferSentOrThrow(context: string, iceRestart: boolean = false): Promise<void> {
    const isOfferSent: boolean = await this.sendOffer(iceRestart);
    if (isOfferSent) return;

    throw new Error(`[WebrtcAudioService] Offer was not delivered (${context})`);
  }

  private observeReplyDelivery(delivery: Promise<boolean>, peer: RTCPeerConnection, signal: string): void {
    void delivery.then((delivered: boolean): void => {
      if (delivered || !this.isCurrentPeer(peer)) return;
      console.warn('[WebrtcAudioService] Reply delivery not confirmed', {signal, orderId: this.orderId});
      this.scheduleReconnect();
    }).catch((): void => {
      if (!this.isCurrentPeer(peer)) return;
      console.warn('[WebrtcAudioService] Reply delivery failed', {signal, orderId: this.orderId});
      this.scheduleReconnect();
    });
  }

  private async sendAnswerWithDiagnostics(peer: RTCPeerConnection, partnerId: number, answer: RTCSessionDescriptionInit, orderId: number): Promise<boolean> {
    const sendResult: AudioCallReliableSendResult = await this.webSocketController.emitAudioCallAnswer(
      partnerId, answer, orderId, (): boolean => this.isCurrentPeer(peer),
    );
    if (!this.isCurrentPeer(peer)) return false;
    if (sendResult.delivered) return true;

    console.warn('[WebrtcAudioService] Answer was not delivered', sendResult);
    return false;
  }

  private resolveReliableSignalMetaStatus(
    meta: AudioCallReliableMeta | null,
    eventLabel: 'offer' | 'answer' | 'candidate',
  ): AudioCallDeliveryStatus | null {
    if (!meta) return null;

    if (!this.partnerId || !this.orderId) {
      console.warn(`[WebrtcAudioService] ${eventLabel} ignored: call context is missing`, {
        partnerId: this.partnerId,
        orderId: this.orderId,
        meta,
      });
      return 'ignored';
    }
    if (!meta.from_id || !Number.isFinite(meta.from_id)) {
      console.warn(`[WebrtcAudioService] ${eventLabel} ignored: invalid sender id`, { meta });
      return 'ignored';
    }
    if (meta.from_id !== this.partnerId) {
      console.warn(`[WebrtcAudioService] ${eventLabel} ignored: partner mismatch`, {
        expected_partner_id: this.partnerId,
        received_partner_id: meta.from_id,
        order_id: meta.order_id,
      });
      return 'ignored';
    }
    if (typeof meta.order_id !== 'number' || !Number.isFinite(meta.order_id)) {
      console.warn(`[WebrtcAudioService] ${eventLabel} ignored: order id is missing in reliable envelope`, { meta });
      return 'ignored';
    }
    if (meta.order_id !== this.orderId) {
      console.warn(`[WebrtcAudioService] ${eventLabel} ignored: order mismatch`, {
        expected_order_id: this.orderId,
        received_order_id: meta.order_id,
        partner_id: meta.from_id,
      });
      return 'ignored';
    }

    return null;
  }

  private async handleReceivedOffer(offer: RTCSessionDescriptionInit, meta: AudioCallReliableMeta | null = null): Promise<AudioCallDeliveryStatus> {
    this.trackDebugStatus(
      'offer.received',
      'call.audioCall.debugStatus.received',
      'neutral',
      'call.audioCall.debugEvents.offer',
      {
        message_id: meta?.message_id ?? null,
        order_id: meta?.order_id ?? null,
      },
    );
    const metaStatus: AudioCallDeliveryStatus | null = this.resolveReliableSignalMetaStatus(meta, 'offer');
    if (metaStatus) {
      this.trackReliableReceiveStatus('offer', metaStatus, meta);
      return metaStatus;
    }

    if (!this.peerConnection) {
      console.warn('[WebrtcAudioService] offer ignored: peer connection missing');
      this.trackReliableReceiveStatus('offer', 'failed', meta, { reason: 'peer_connection_missing' });
      return 'failed';
    }
    const peer: RTCPeerConnection = this.peerConnection;
    try {
      // Wait only for local tracks, not initConnection (which itself waits for signaling).
      if (this.mediaInitialization?.peer === peer) {
        await this.mediaInitialization.ready;
        if (!this.isCurrentPeer(peer)) return 'ignored';
      }
      this.allCandidates = { local: [], remote: [] };

      if (peer.signalingState === 'have-local-offer') {
        const rollbackDescription: RTCSessionDescriptionInit = { type: 'rollback' };
        await peer.setLocalDescription(rollbackDescription);
        if (!this.isCurrentPeer(peer)) return 'ignored';
      } else if (peer.signalingState !== 'stable') {
        console.warn('[WebrtcAudioService] offer ignored: signaling state is not stable', {
          signalingState: this.peerConnection.signalingState,
          message_id: meta?.message_id ?? null,
          order_id: meta?.order_id ?? null,
        });
        this.trackReliableReceiveStatus('offer', 'failed', meta, { reason: 'signaling_state_not_stable' });
        return 'failed';
      }

      await peer.setRemoteDescription(offer);
      if (!this.isCurrentPeer(peer)) return 'ignored';

      await this.addBufferedCandidatesToPC();
      if (!this.isCurrentPeer(peer)) return 'ignored';

      const answer: RTCSessionDescriptionInit = await peer.createAnswer();
      if (!this.isCurrentPeer(peer)) return 'ignored';
      await peer.setLocalDescription(answer);
      if (!this.isCurrentPeer(peer)) return 'ignored';
      if (!this.partnerId || !this.orderId) {
        console.warn('[WebrtcAudioService] offer failed: partner id is missing while sending answer');
        return 'failed';
      }

      // Receipt acknowledges local processing, not the peer's processing of our reply.
      this.observeReplyDelivery(
        this.sendAnswerWithDiagnostics(peer, this.partnerId, answer, this.orderId), peer, 'answer',
      );
      this.trackReliableReceiveStatus('offer', 'processed', meta);
      return 'processed';

    } catch (error) {
      if (!this.isCurrentPeer(peer)) return 'ignored';
      console.error(error);
      this.trackReliableReceiveStatus('offer', 'failed', meta, error);
      return 'failed';
    }
  }

  private async handleReceivedAnswer(answer: RTCSessionDescriptionInit, meta: AudioCallReliableMeta | null = null): Promise<AudioCallDeliveryStatus> {
    this.trackDebugStatus(
      'answer.received',
      'call.audioCall.debugStatus.received',
      'neutral',
      'call.audioCall.debugEvents.answer',
      {
        message_id: meta?.message_id ?? null,
        order_id: meta?.order_id ?? null,
      },
    );
    const metaStatus: AudioCallDeliveryStatus | null = this.resolveReliableSignalMetaStatus(meta, 'answer');
    if (metaStatus) {
      this.trackReliableReceiveStatus('answer', metaStatus, meta);
      return metaStatus;
    }

    if (!this.peerConnection) {
      console.warn('[WebrtcAudioService] answer ignored: peer connection missing');
      this.trackReliableReceiveStatus('answer', 'failed', meta, { reason: 'peer_connection_missing' });
      return 'failed';
    }
    if (this.peerConnection.signalingState === 'stable') {
      console.warn('[WebrtcAudioService] answer ignored: signaling state is stable');
      this.trackReliableReceiveStatus('answer', 'ignored', meta, { reason: 'signaling_state_stable' });
      return 'ignored';
    }
    const peer: RTCPeerConnection = this.peerConnection;
    try {
      await peer.setRemoteDescription(answer);
      if (!this.isCurrentPeer(peer)) return 'ignored';

      if (!this.isShared) {
        await this.addBufferedCandidatesToPC();
        if (!this.isCurrentPeer(peer)) return 'ignored';
      }
      this.trackReliableReceiveStatus('answer', 'processed', meta);
      return 'processed';
    } catch (error) {
      if (!this.isCurrentPeer(peer)) return 'ignored';
      console.error(error);
      this.trackReliableReceiveStatus('answer', 'failed', meta, error);
      return 'failed';
    }
  }

  private isCandidateForCurrentCall(candidate: RTCIceCandidateInit): boolean {
    const candidateOrderId: unknown = Reflect.get(candidate, 'order_id');
    if (typeof candidateOrderId !== 'number' || !Number.isFinite(candidateOrderId)) {
      if (!environment.production) {
        console.warn('[WebrtcAudioService] candidate ignored: order_id is missing', {
          expected_order_id: this.orderId,
        });
      }
      return false;
    }

    if (!this.orderId) return false;
    return candidateOrderId === this.orderId;
  }

  private async handleReceivedCandidate(
    candidate: RTCIceCandidateInit,
    meta: AudioCallReliableMeta | null = null,
  ): Promise<AudioCallDeliveryStatus> {
    this.trackDebugStatus(
      'candidate.received',
      'call.audioCall.debugStatus.received',
      'neutral',
      'call.audioCall.debugEvents.candidate',
      {
        message_id: meta?.message_id ?? null,
        order_id: meta?.order_id ?? null,
      },
      false,
    );
    const metaStatus: AudioCallDeliveryStatus | null = this.resolveReliableSignalMetaStatus(meta, 'candidate');
    if (metaStatus) {
      this.trackReliableReceiveStatus('candidate', metaStatus, meta, candidate, false);
      return metaStatus;
    }

    if (!this.isCandidateForCurrentCall(candidate)) {
      if (!environment.production) {
        console.warn('[WebrtcAudioService] candidate ignored: order mismatch', {
          expected_order_id: this.orderId,
          received_order_id: Reflect.get(candidate, 'order_id'),
        });
      }
      return 'ignored';
    }

    if (!this.peerConnection) {
      console.warn('[WebrtcAudioService] candidate ignored: peer connection missing');
      return 'ignored';
    }

    if (!this.isValidCandidate(candidate)) {
      return 'ignored';
    }

    let newRemoteCandidate: RTCIceCandidate;
    try {
      newRemoteCandidate = new RTCIceCandidate(candidate);
    } catch (error) {
      console.warn('[WebrtcAudioService] candidate ignored: unable to create RTCIceCandidate', error);
      return 'ignored';
    }
    const hasDuplicateRemoteCandidate: boolean = this.allCandidates.remote.some((item: RTCIceCandidate): boolean => {
      return item.candidate === newRemoteCandidate.candidate
        && item.sdpMid === newRemoteCandidate.sdpMid
        && item.sdpMLineIndex === newRemoteCandidate.sdpMLineIndex;
    });
    if (hasDuplicateRemoteCandidate) {
      return 'processed';
    }

    const hasBufferedDuplicateCandidate: boolean = this.iceCandidatesBuffer.some((item: RTCIceCandidate): boolean => {
      return item.candidate === newRemoteCandidate.candidate
        && item.sdpMid === newRemoteCandidate.sdpMid
        && item.sdpMLineIndex === newRemoteCandidate.sdpMLineIndex;
    });
    if (hasBufferedDuplicateCandidate) {
      return 'processed';
    }

    const peer: RTCPeerConnection = this.peerConnection;
    if (peer.remoteDescription) {
      try {
        await peer.addIceCandidate(newRemoteCandidate);
        if (!this.isCurrentPeer(peer)) return 'ignored';
        this.allCandidates.remote.push(newRemoteCandidate);
        this.trackReliableReceiveStatus('candidate', 'processed', meta, candidate, false);
        return 'processed';
      } catch (error) {
        console.warn('[WebrtcAudioService] addIceCandidate failed, candidate ignored', error);
        this.trackReliableReceiveStatus('candidate', 'failed', meta, error, false);
        return 'failed';
      }
    } else {
      this.iceCandidatesBuffer.push(newRemoteCandidate);
      this.trackReliableReceiveStatus('candidate', 'processed', meta, { buffered: true }, false);
      return 'processed';
    }
  }

  private async addBufferedCandidatesToPC(): Promise<void> {
    const peer: RTCPeerConnection | null = this.peerConnection;
    if (!peer) return;

    // A stale ICE generation must not discard the remaining viable candidates.
    while (this.iceCandidatesBuffer.length > 0) {
      if (this.peerConnection !== peer) return;
      const candidate: RTCIceCandidate | undefined = this.iceCandidatesBuffer.shift();
      if (!candidate) return;
      try {
        await peer.addIceCandidate(candidate);
        if (this.peerConnection !== peer) return;
        this.allCandidates.remote.push(candidate);
      } catch {
        console.warn('[WebrtcAudioService] Buffered ICE candidate rejected');
      }
    }
  }

  private enqueueReliableAudioCallEvent(processEvent: () => Promise<void>): void {
    const peer: RTCPeerConnection | null = this.peerConnection;
    const nextEvent: Promise<void> = this.reliableAudioCallEventQueue
      .catch((): void => undefined)
      .then(async (): Promise<void> => {
        if (!peer || !this.isCurrentPeer(peer)) return;
        await processEvent();
      });

    this.reliableAudioCallEventQueue = nextEvent.catch((error: unknown): void => {
      console.error('Queued audio call event handling error!', error);
    });
  }

  private subscribeAudioCallEvents(): void {
    if (this.audioCallEventSubscription) {
      return console.error('audioCallEventSubscription already initialized!');
    }

    this.audioCallEventSubscription = this.webSocketController.audioCallEvent.subscribe((res: any): void => {
      this.enqueueReliableAudioCallEvent(async (): Promise<void> => {
        try {
          switch (res.type) {
            case SOCKET_EVENT_NAMES.AUDIO_CALL_OFFER:
              await this.processReliableAudioCallEvent(
                res?.meta ?? null,
                async (): Promise<AudioCallDeliveryStatus> => await this.handleReceivedOffer(res.offer, res?.meta ?? null),
              );
              break;
            case SOCKET_EVENT_NAMES.AUDIO_CALL_ANSWER:
              await this.processReliableAudioCallEvent(
                res?.meta ?? null,
                async (): Promise<AudioCallDeliveryStatus> => await this.handleReceivedAnswer(res.answer, res?.meta ?? null),
              );
              break;
            case SOCKET_EVENT_NAMES.AUDIO_CALL_CANDIDATE:
              try {
                await this.processReliableAudioCallEvent(
                  res?.meta ?? null,
                  async (): Promise<AudioCallDeliveryStatus> => await this.handleReceivedCandidate(res.candidate, res?.meta ?? null),
                );
              } catch (error) {
                // ICE candidates are high-frequency and may arrive out-of-order; this must not restart the whole call flow.
                console.warn('[WebrtcAudioService] candidate event processing error (ignored)', error);
              }
              break;
            case SOCKET_EVENT_NAMES.AUDIO_CALL_REQUEST_RECONNECT:
              await this.processReliableAudioCallEvent(
                res?.meta ?? null,
                async (): Promise<AudioCallDeliveryStatus> => await this.handleReceivedRequestReconnect(res.request),
              );
              break;
            case SOCKET_EVENT_NAMES.AUDIO_CALL_CUSTOMER_READY_RECONNECT:
              await this.processReliableAudioCallEvent(
                res?.meta ?? null,
                async (): Promise<AudioCallDeliveryStatus> => await this.handleReceivedCustomerReadyReconnect(res.response),
              );
              break;
          }
        } catch (error) {
          console.error('Event handling error!', error);
          if (typeof this.errorCallback === 'function') this.errorCallback(error);
        }
      });
    });
  }

  private requestReconnect(partnerId: number | null, orderId: number | null): void {
    if (!partnerId) {
      return console.error('Not found partnerId!', partnerId);
    }
    if (!orderId) {
      return console.error('Not found orderId!', orderId);
    }

    if (this.streamStartTime <= 0 && !this.isConnected) {
      this.audioService.playConnectingSound();
    }
    void this.emitAudioCallRequestReconnectWithDiagnostics(partnerId, orderId);
  }

  private async emitAudioCallRequestReconnectWithDiagnostics(partnerId: number, orderId: number): Promise<void> {
    const peer: RTCPeerConnection | null = this.peerConnection;
    if (!peer) return;
    const sendResult: AudioCallReliableSendResult = await this.webSocketController.emitAudioCallRequestReconnect(
      partnerId, orderId, (): boolean => this.isCurrentPeer(peer),
    );
    if (!this.isCurrentPeer(peer)) return;
    if (sendResult.delivered) return;

    console.warn('[WebrtcAudioService] Request reconnect was not delivered', {
      partnerId,
      orderId,
      sendResult,
    });
  }

  private async handleReceivedRequestReconnect(request: EventAudioCallRequestReconnect): Promise<AudioCallDeliveryStatus> {
    this.trackDebugStatus(
      'reconnect.request.received',
      'call.audioCall.debugStatus.received',
      'neutral',
      'call.audioCall.debugEvents.requestReconnect',
      request,
    );
    if (!request.from_id) {
      console.error('Not found request.from_id!', request.from_id);
      this.trackReliableReceiveStatus('requestReconnect', 'ignored', null, request);
      return 'ignored';
    }
    if (!request.order_id) {
      console.error('Not found request.order_id!', request.order_id);
      this.trackReliableReceiveStatus('requestReconnect', 'ignored', null, request);
      return 'ignored';
    }
    if (!this.peerConnection || this.peerConnection.connectionState === 'closed' || this.peerConnection.signalingState === 'closed') {
      console.warn('[WebrtcAudioService] Request reconnect ignored: peer connection closed');
      this.trackReliableReceiveStatus('requestReconnect', 'failed', null, {reason: 'peer_connection_closed', request});
      return 'failed';
    }

    if (!this.isReconnectEventForCurrentCall(request.from_id, request.order_id)) {
      console.warn('[WebrtcAudioService] Request reconnect ignored: order/partner mismatch', {
        from_id: request.from_id,
        order_id: request.order_id,
        expected_partner_id: this.partnerId,
        expected_order_id: this.orderId,
      });
      return 'ignored';
    }

    const peer: RTCPeerConnection = this.peerConnection;
    this.observeReplyDelivery(
      this.sendCustomerReadyReconnect(peer, request.from_id, request.order_id), peer, 'ready_reconnect',
    );
    this.trackReliableReceiveStatus('requestReconnect', 'processed', null, request);
    return 'processed';
  }

  private async sendCustomerReadyReconnect(peer: RTCPeerConnection, partnerId: number, orderId: number): Promise<boolean> {
    if (!partnerId) {
      console.error('Not found partnerId!', partnerId);
      return false;
    }
    if (!orderId) {
      console.error('Not found orderId!', orderId);
      return false;
    }

    const sendResult: AudioCallReliableSendResult = await this.webSocketController.emitAudioCallCustomerReadyReconnect(
      partnerId,
      true,
      orderId,
      (): boolean => this.isCurrentPeer(peer),
      this.callSessionService.getAttempt(orderId, this.callSessionService.selectedOrder?.myUserId),
    );
    if (!this.isCurrentPeer(peer)) return false;
    if (!sendResult.delivered) {
      console.warn('[WebrtcAudioService] Customer ready reconnect was not delivered', sendResult);
      return false;
    }
    return true;
  }

  private async handleReceivedCustomerReadyReconnect(request: EventAudioCallCustomerReadyReconnect): Promise<AudioCallDeliveryStatus> {
    this.trackDebugStatus(
      'reconnect.ready.received',
      'call.audioCall.debugStatus.received',
      'neutral',
      'call.audioCall.debugEvents.customerReadyReconnect',
      request,
    );
    if (!request.from_id) {
      console.error('Not found request.from_id!');
      this.trackReliableReceiveStatus('customerReadyReconnect', 'ignored', null, request);
      return 'ignored';
    }
    if (!request.order_id) {
      console.error('Not found request.order_id!');
      return 'ignored';
    }
    if (!this.orderId) {
      console.error('Not found this.orderId!');
      return 'ignored';
    }
    if (!this.peerConnection || this.peerConnection.connectionState === 'closed' || this.peerConnection.signalingState === 'closed') {
      console.warn('[WebrtcAudioService] Customer reconnect ignored: peer connection closed');
      return 'failed';
    }
    if (!request.ready_reconnect) {
      console.warn('[WebrtcAudioService] Customer not ready to reconnect', {
        from_id: request.from_id,
        order_id: request.order_id,
      });
      this.trackReliableReceiveStatus('customerReadyReconnect', 'ignored', null, request);
      return 'ignored';
    }
    if (!this.isReconnectEventForCurrentCall(request.from_id, request.order_id)) {
      console.warn('[WebrtcAudioService] Customer reconnect ignored: order/partner mismatch', {
        from_id: request.from_id,
        order_id: request.order_id,
        expected_partner_id: this.partnerId,
        expected_order_id: this.orderId,
      });
      return 'ignored';
    }

    const peer: RTCPeerConnection = this.peerConnection;
    const pending: {delivery: Promise<boolean>} | null = await this.prepareAndSendOffer(true);
    if (!this.isCurrentPeer(peer)) return 'ignored';
    if (!pending) return 'failed';
    this.observeReplyDelivery(pending.delivery, peer, 'reconnect_offer');
    this.trackReliableReceiveStatus('customerReadyReconnect', 'processed', null, request);
    return 'processed';
  }

  private trackDebugStatus(
    stage: string,
    actionKey: string,
    tone: AudioCallConnectionDebugTone,
    eventKey?: string,
    detail?: unknown,
    updateCurrentStatus: boolean = true,
    params?: AudioCallConnectionDebugParams,
  ): void {
    this.audioCallConnectionDebugService.track(
      'webrtc',
      stage,
      {
        actionKey,
        ...(eventKey ? {eventKey} : {}),
        ...(params ? {params} : {}),
        tone,
      },
      {
        detail,
        updateCurrentStatus,
      },
    );
  }

  private trackReliableReceiveStatus(
    eventLabel: 'offer' | 'answer' | 'candidate' | 'requestReconnect' | 'customerReadyReconnect',
    status: AudioCallDeliveryStatus,
    meta?: AudioCallReliableMeta | null,
    detail?: unknown,
    updateCurrentStatus: boolean = true,
  ): void {
    const eventKey: string = eventLabel === 'offer'
      ? 'call.audioCall.debugEvents.offer'
      : eventLabel === 'answer'
        ? 'call.audioCall.debugEvents.answer'
        : eventLabel === 'candidate'
          ? 'call.audioCall.debugEvents.candidate'
          : eventLabel === 'requestReconnect'
            ? 'call.audioCall.debugEvents.requestReconnect'
            : 'call.audioCall.debugEvents.customerReadyReconnect';

    if (status === 'processed' || status === 'duplicate') {
      this.trackDebugStatus(
        `${eventLabel}.confirmed`,
        'call.audioCall.debugStatus.confirmed',
        'success',
        eventKey,
        {
          meta,
          detail,
        },
        updateCurrentStatus,
      );
      return;
    }

    if (status === 'ignored') {
      this.trackDebugStatus(
        `${eventLabel}.ignored`,
        'call.audioCall.debugStatus.ignored',
        'warning',
        eventKey,
        {
          meta,
          detail,
        },
        updateCurrentStatus,
      );
      return;
    }

    if (status === 'failed') {
      this.trackDebugStatus(
        `${eventLabel}.failed`,
        'call.audioCall.debugStatus.failed',
        'error',
        eventKey,
        {
          meta,
          detail,
        },
        updateCurrentStatus,
      );
    }
  }

  private isReconnectEventForCurrentCall(fromId: number, orderId: number): boolean {
    if (!fromId || !orderId) return false;
    if (!this.partnerId || !this.orderId) return false;
    return this.partnerId === fromId && this.orderId === orderId;
  }

  private isValidCandidate(candidate: RTCIceCandidateInit | RTCIceCandidate): boolean {
    const candidateValue: unknown = Reflect.get(candidate, 'candidate');
    const sdpMid: unknown = Reflect.get(candidate, 'sdpMid');
    const sdpMLineIndex: unknown = Reflect.get(candidate, 'sdpMLineIndex');
    const isValid = typeof candidateValue === 'string'
      && typeof sdpMid === 'string'
      && typeof sdpMLineIndex === 'number';

    if (!isValid) {
      console.warn('INCORRECT ICE CANDIDATE FOUND!', candidate);
      if (typeof candidateValue === 'string') {
      }
    }

    return isValid;
  }

  private buildRtcConfiguration(): RTCConfiguration {
    const config = this.peerServerConfigDebug?.config;
    const iceServers: RTCIceServer[] = this.normalizeIceServers(Reflect.get(config ?? {}, 'iceServers'));
    const iceTransportPolicy: RTCIceTransportPolicy | undefined = this.normalizeIceTransportPolicy(Reflect.get(config ?? {}, 'iceTransportPolicy'));
    const rtcConfiguration: RTCConfiguration = {};

    if (iceServers.length > 0) {
      rtcConfiguration.iceServers = iceServers;
    }
    if (iceTransportPolicy) {
      rtcConfiguration.iceTransportPolicy = iceTransportPolicy;
    }

    return rtcConfiguration;
  }

  private normalizeIceServers(value: unknown): RTCIceServer[] {
    if (!Array.isArray(value)) return [];

    const servers: RTCIceServer[] = [];

    value.forEach((item: unknown): void => {
      if (!item || typeof item !== 'object') return;

      const urlsValue: unknown = Reflect.get(item, 'urls');
      if (typeof urlsValue !== 'string' && !Array.isArray(urlsValue)) return;

      const server: RTCIceServer = { urls: urlsValue };
      const usernameValue: unknown = Reflect.get(item, 'username');
      if (typeof usernameValue === 'string') {
        server.username = usernameValue;
      }
      const credentialValue: unknown = Reflect.get(item, 'credential');
      if (typeof credentialValue === 'string') {
        server.credential = credentialValue;
      }

      servers.push(server);
    });

    return servers;
  }

  private normalizeIceTransportPolicy(value: unknown): RTCIceTransportPolicy | undefined {
    if (value === 'all' || value === 'relay') {
      return value;
    }

    return undefined;
  }

  private async createVibration(type: NotificationType): Promise<void> {
    if (!this.platformService.isDevice) return;

    await this.hapticsService.notification(type);
  }
}
